import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import IntranetLayout from "@/components/IntranetLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Wrench, AlertTriangle, CheckCircle2, Ship, Plus, Search, Calendar, Clock,
  Settings, Package, ShoppingCart, BarChart3, Filter, FileText, Cog,
  Gauge, ChevronRight, ChevronDown, Eye, Trash2, ArrowUpDown, RefreshCw,
  AlertCircle, TrendingUp, Shield, Anchor, ClipboardList, Send, GitBranch,
  PauseCircle, XCircle, Lock, Unlock, FolderTree, Users, Activity,
  Thermometer, BookOpen, Download, Printer, ShieldCheck, Zap, List,
  HardHat, FileWarning, CircleDot, Database, X, Bug, History, ClipboardCheck,
  CheckSquare, Square, MinusSquare, Rocket
} from "lucide-react";
import {
  EQUIPMENT_CATEGORIES, CRITICALITY, JOB_TYPES, DEPARTMENTS,
  JOB_STATUS_CONFIG, WO_STATUS_CONFIG, PRIORITY_CONFIG, DEFERRAL_STATUS_CONFIG,
  DEFECT_SEVERITY_CONFIG, DEFECT_STATUS_CONFIG, DEFECT_TYPES,
  SPARE_CATEGORIES, PMS_ROLES, CONDITION_PARAMETER_TYPES, CLASS_SOCIETIES,
  TECHNICAL_MANAGER_ROLES, CE_ROLES, VISI_CATEGORIES, SYSTEM_CATEGORIES,
  StatusBadge, PriorityBadge,
} from "./pms/PMSConstants";
import PMSSidebar from "./pms/PMSSidebar";
import PMSDetailPanel from "./pms/PMSDetailPanel";
import PMSCalendar from "./pms/PMSCalendar";
import PMSMyJobs from "./pms/PMSMyJobs";
import PMSJobCompletionDialog from "./pms/PMSJobCompletionDialog";
import { AIRCAT35_SYSTEMS, AIRCAT35_MAINTENANCE_TEMPLATES } from "@/data/aircatPmsComponentTree";

// ─── Helpers ───────────────────────────────────────────────────────────────
const FormField = ({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) => (
  <div className="space-y-1"><Label className="text-xs">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>{children}</div>
);

// ─── Component ─────────────────────────────────────────────────────────────
const EmployeeMaintenancePMS = () => {
  const { user, fullName, employeePermissions } = useAuth();
  const queryClient = useQueryClient();

  const employeeRole = employeePermissions?.employee_role || "Crew";
  const isChiefEngineer = CE_ROLES.includes(employeeRole);
  const isTechnicalManager = TECHNICAL_MANAGER_ROLES.includes(employeeRole);
  const isSuperintendent = employeeRole === "Shoreside Leadership" || employeeRole === "Executive Team" || employeeRole === "Manager";
  const hasFullFleetAccess = isTechnicalManager;
  const canAssign = isChiefEngineer || isTechnicalManager;

  const [activeSection, setActiveSection] = useState(canAssign ? "dashboard" : "my_jobs");
  const [search, setSearch] = useState("");
  const [vesselFilter, setVesselFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTreeVessel, setSelectedTreeVessel] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedEquipment, setExpandedEquipment] = useState<Set<string>>(new Set());

  const [detailType, setDetailType] = useState<"equipment" | "job" | "work_order" | null>(null);
  const [detailItem, setDetailItem] = useState<any>(null);

  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [showAddJob, setShowAddJob] = useState(false);
  const [showAddWO, setShowAddWO] = useState(false);
  const [showAddPart, setShowAddPart] = useState(false);
  const [showLogHours, setShowLogHours] = useState(false);
  const [showDeferDialog, setShowDeferDialog] = useState(false);
  const [deferJob, setDeferJob] = useState<any>(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showAddCondition, setShowAddCondition] = useState(false);
  const [showAddDefect, setShowAddDefect] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completeJob, setCompleteJob] = useState<any>(null);

  const [eqForm, setEqForm] = useState<any>({});
  const [jobForm, setJobForm] = useState<any>({ priority: "medium", department: "engine", job_type: "calendar" });
  const [woForm, setWoForm] = useState<any>({ priority: "medium", department: "engine", work_type: "planned" });
  const [partForm, setPartForm] = useState<any>({ category: "general", unit: "ea", currency: "USD", minimum_quantity: 1, maximum_quantity: 10, reorder_point: 2 });
  const [hoursForm, setHoursForm] = useState<any>({});
  const [deferForm, setDeferForm] = useState<any>({});
  const [assignForm, setAssignForm] = useState<any>({});
  const [conditionForm, setConditionForm] = useState<any>({ parameter_type: "vibration" });
  const [defectForm, setDefectForm] = useState<any>({ severity: "minor", defect_type: "defect" });
  const [visiVessel, setVisiVessel] = useState<string | null>(null);
  const [visiChecks, setVisiChecks] = useState<Record<string, "pass" | "fail" | "na">>({});

  // ─── Queries ─────────────────────────────────────────────────────────────
  const { data: vessels = [] } = useQuery({
    queryKey: ["fleet-vessels"],
    queryFn: async () => { const { data } = await supabase.from("fleet_vessels").select("*").order("name"); return data ?? []; },
  });
  const { data: vesselAssignments = [] } = useQuery({
    queryKey: ["pms-vessel-assignments", user?.id],
    queryFn: async () => { if (!user?.id) return []; const { data } = await supabase.from("pms_vessel_assignments").select("*").eq("user_id", user.id); return data ?? []; },
    enabled: !!user?.id,
  });
  const { data: allAssignments = [] } = useQuery({
    queryKey: ["pms-all-assignments"],
    queryFn: async () => { const { data } = await supabase.from("pms_vessel_assignments").select("*").order("vessel_name,pms_role"); return data ?? []; },
    enabled: isTechnicalManager,
  });
  // Crew members for assignment (profiles joined with vessel assignments)
  const { data: crewMembers = [] } = useQuery({
    queryKey: ["pms-crew-members"],
    queryFn: async () => {
      const { data: assignments } = await supabase.from("pms_vessel_assignments").select("user_id, vessel_id, vessel_name, pms_role").order("vessel_name,pms_role");
      if (!assignments || assignments.length === 0) return [];
      const userIds = [...new Set(assignments.map(a => a.user_id))];
      const { data: profiles } = await supabase.from("profiles").select("user_id, full_name").in("user_id", userIds);
      const nameMap = new Map((profiles || []).map(p => [p.user_id, p.full_name]));
      return assignments.map(a => ({ ...a, full_name: nameMap.get(a.user_id) || a.user_id.slice(0, 8) + "…" }));
    },
  });
  // Employee list for assignment dialog
  const { data: employeeList = [] } = useQuery({
    queryKey: ["pms-employee-list"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("user_id, full_name").order("full_name");
      return data ?? [];
    },
    enabled: canAssign,
  });

  const accessibleVesselIds = useMemo(() => {
    if (hasFullFleetAccess) return vessels.map((v: any) => v.id);
    if (vesselAssignments.length > 0) return vesselAssignments.map((a: any) => a.vessel_id);
    return vessels.map((v: any) => v.id);
  }, [hasFullFleetAccess, vesselAssignments, vessels]);
  const accessibleVessels = useMemo(() => vessels.filter((v: any) => accessibleVesselIds.includes(v.id)), [vessels, accessibleVesselIds]);

  const { data: equipment = [], isLoading: loadingEquipment } = useQuery({
    queryKey: ["pms-equipment"],
    queryFn: async () => { const { data } = await supabase.from("pms_equipment").select("*").eq("is_active", true).order("vessel_name,category,equipment_name"); return data ?? []; },
  });
  const { data: jobs = [], isLoading: loadingJobs } = useQuery({
    queryKey: ["pms-jobs"],
    queryFn: async () => { const { data } = await supabase.from("pms_jobs").select("*").order("next_due_date"); return data ?? []; },
  });
  const { data: workOrders = [], isLoading: loadingWO } = useQuery({
    queryKey: ["pms-work-orders"],
    queryFn: async () => { const { data } = await supabase.from("pms_work_orders").select("*").order("created_at", { ascending: false }); return data ?? []; },
  });
  const { data: spareParts = [], isLoading: loadingParts } = useQuery({
    queryKey: ["pms-spare-parts"],
    queryFn: async () => { const { data } = await supabase.from("pms_spare_parts").select("*").order("vessel_name,category,description"); return data ?? []; },
  });
  const { data: runningHours = [] } = useQuery({
    queryKey: ["pms-running-hours"],
    queryFn: async () => { const { data } = await supabase.from("pms_running_hours").select("*").order("recorded_date", { ascending: false }).limit(200); return data ?? []; },
  });
  const { data: deferrals = [] } = useQuery({
    queryKey: ["pms-deferrals"],
    queryFn: async () => { const { data } = await supabase.from("pms_deferrals").select("*").order("created_at", { ascending: false }); return data ?? []; },
  });
  const { data: conditionData = [] } = useQuery({
    queryKey: ["pms-condition-monitoring"],
    queryFn: async () => { const { data } = await supabase.from("pms_condition_monitoring").select("*").order("recorded_date", { ascending: false }).limit(500); return data ?? []; },
  });
  const { data: vendors = [] } = useQuery({
    queryKey: ["vendors-list"],
    queryFn: async () => { const { data } = await supabase.from("vendors").select("id,name").eq("status", "approved").order("name"); return data ?? []; },
  });
  const { data: defects = [], isLoading: loadingDefects } = useQuery({
    queryKey: ["pms-defects"],
    queryFn: async () => { const { data } = await supabase.from("pms_defects").select("*").order("reported_date", { ascending: false }); return data ?? []; },
  });

  // ─── Role-based filtering ───────────────────────────────────────────────
  const roleFilteredEquipment = useMemo(() => equipment.filter((e: any) => accessibleVesselIds.includes(e.vessel_id)), [equipment, accessibleVesselIds]);
  const roleFilteredJobs = useMemo(() => jobs.filter((j: any) => accessibleVesselIds.includes(j.vessel_id)), [jobs, accessibleVesselIds]);
  const roleFilteredWOs = useMemo(() => workOrders.filter((w: any) => accessibleVesselIds.includes(w.vessel_id)), [workOrders, accessibleVesselIds]);
  const roleFilteredParts = useMemo(() => spareParts.filter((p: any) => !p.vessel_id || accessibleVesselIds.includes(p.vessel_id)), [spareParts, accessibleVesselIds]);
  const roleFilteredDeferrals = useMemo(() => deferrals.filter((d: any) => accessibleVesselIds.includes(d.vessel_id)), [deferrals, accessibleVesselIds]);
  const roleFilteredCondition = useMemo(() => conditionData.filter((c: any) => accessibleVesselIds.includes(c.vessel_id)), [conditionData, accessibleVesselIds]);
  const roleFilteredDefects = useMemo(() => defects.filter((d: any) => accessibleVesselIds.includes(d.vessel_id)), [defects, accessibleVesselIds]);

  // ─── Mutations ───────────────────────────────────────────────────────────
  const addEquipment = useMutation({
    mutationFn: async (form: any) => {
      const vessel = vessels.find((v: any) => v.id === form.vessel_id);
      const { error } = await supabase.from("pms_equipment").insert({ ...form, vessel_name: vessel?.name || "" });
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-equipment"] }); toast({ title: "Equipment registered" }); setShowAddEquipment(false); setEqForm({}); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const addJob = useMutation({
    mutationFn: async (form: any) => {
      const vessel = vessels.find((v: any) => v.id === form.vessel_id);
      const { error } = await supabase.from("pms_jobs").insert({ ...form, job_number: "", vessel_name: vessel?.name || "" });
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-jobs"] }); toast({ title: "Maintenance job created" }); setShowAddJob(false); setJobForm({ priority: "medium", department: "engine", job_type: "calendar" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const addWorkOrder = useMutation({
    mutationFn: async (form: any) => {
      const vessel = vessels.find((v: any) => v.id === form.vessel_id);
      const eq = equipment.find((e: any) => e.id === form.equipment_id);
      const { error } = await supabase.from("pms_work_orders").insert({ ...form, work_order_number: "", vessel_name: vessel?.name || "", equipment_name: eq?.equipment_name || "" });
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-work-orders"] }); toast({ title: "Work order created" }); setShowAddWO(false); setWoForm({ priority: "medium", department: "engine", work_type: "planned" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const addSparePart = useMutation({
    mutationFn: async (form: any) => {
      const vessel = vessels.find((v: any) => v.id === form.vessel_id);
      const { error } = await supabase.from("pms_spare_parts").insert({ ...form, vessel_name: vessel?.name || "" });
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-spare-parts"] }); toast({ title: "Spare part added" }); setShowAddPart(false); setPartForm({ category: "general", unit: "ea", currency: "USD", minimum_quantity: 1, maximum_quantity: 10, reorder_point: 2 }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const logRunningHours = useMutation({
    mutationFn: async (form: any) => {
      const { error } = await supabase.from("pms_running_hours").insert({
        equipment_id: form.equipment_id, vessel_id: form.vessel_id, hours_reading: form.hours_reading,
        recorded_by: user?.id, recorded_by_name: fullName,
        recorded_date: form.recorded_date || new Date().toISOString().split("T")[0], notes: form.notes,
      });
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-running-hours"] }); toast({ title: "Running hours logged" }); setShowLogHours(false); setHoursForm({}); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateWOStatus = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { error } = await supabase.from("pms_work_orders").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-work-orders"] }); toast({ title: "Work order updated" }); },
  });

  const updateJobStatus = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { error } = await supabase.from("pms_jobs").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pms-jobs"] }),
  });

  const createPOFromWO = useMutation({
    mutationFn: async (wo: any) => {
      const { data, error } = await supabase.from("purchase_orders").insert({
        order_number: "", title: `Parts for WO ${wo.work_order_number}: ${wo.title}`,
        description: `Procurement request generated from Work Order ${wo.work_order_number}.\n\nEquipment: ${wo.equipment_name}\nVessel: ${wo.vessel_name}`,
        order_type: "material", scope: wo.vessel_id ? "vessel" : "corporate",
        vessel_id: wo.vessel_id, vessel_name: wo.vessel_name,
        requested_by: user?.id || "", requested_by_name: fullName, priority: wo.priority, status: "draft",
      }).select("id, order_number").single();
      if (error) throw error;
      await supabase.from("pms_work_orders").update({ linked_purchase_order_id: data.id, linked_po_number: data.order_number }).eq("id", wo.id);
      return data;
    },
    onSuccess: (data) => { queryClient.invalidateQueries({ queryKey: ["pms-work-orders"] }); toast({ title: "Purchase order created", description: `PO ${data.order_number} linked` }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const autoReorderPart = useMutation({
    mutationFn: async (part: any) => {
      const reorderQty = part.maximum_quantity - part.quantity_on_hand;
      const { data, error } = await supabase.from("purchase_orders").insert({
        order_number: "", title: `Auto-Reorder: ${part.description} (${part.part_number})`,
        description: `Automatic reorder triggered — stock at ${part.quantity_on_hand} ${part.unit} (min: ${part.minimum_quantity}, reorder point: ${part.reorder_point}).`,
        order_type: "material", scope: part.vessel_id ? "vessel" : "corporate",
        vessel_id: part.vessel_id || null, vessel_name: part.vessel_name || "",
        vendor_id: part.preferred_vendor_id || null, vendor_name: part.preferred_vendor_name || null,
        requested_by: user?.id || "", requested_by_name: fullName, priority: part.is_critical_spare ? "critical" : "medium", status: "draft",
        estimated_total: part.unit_cost ? part.unit_cost * reorderQty : null,
      }).select("id, order_number").single();
      if (error) throw error;
      await supabase.from("pms_spare_parts").update({ last_ordered_date: new Date().toISOString().split("T")[0] }).eq("id", part.id);
      return data;
    },
    onSuccess: (data) => { queryClient.invalidateQueries({ queryKey: ["pms-spare-parts"] }); toast({ title: "Reorder PO Created", description: `${data.order_number} submitted` }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const submitDeferral = useMutation({
    mutationFn: async (form: any) => {
      const { error } = await supabase.from("pms_deferrals").insert(form);
      if (error) throw error;
      await supabase.from("pms_jobs").update({ status: "deferred", defer_reason: form.reason }).eq("id", form.job_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pms-deferrals"] });
      queryClient.invalidateQueries({ queryKey: ["pms-jobs"] });
      toast({ title: "Deferral request submitted" });
      setShowDeferDialog(false); setDeferJob(null); setDeferForm({});
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const approveDeferral = useMutation({
    mutationFn: async ({ id, role, approved, notes }: { id: string; role: "ce" | "tm"; approved: boolean; notes?: string }) => {
      const updates: any = {};
      if (role === "ce") {
        updates.ce_approved = approved; updates.ce_approved_by = user?.id; updates.ce_approved_by_name = fullName;
        updates.ce_approved_at = new Date().toISOString(); updates.ce_notes = notes || null;
        updates.status = approved ? "ce_approved" : "rejected";
      } else {
        updates.tm_approved = approved; updates.tm_approved_by = user?.id; updates.tm_approved_by_name = fullName;
        updates.tm_approved_at = new Date().toISOString(); updates.tm_notes = notes || null;
        updates.status = approved ? "approved" : "rejected";
      }
      const { error } = await supabase.from("pms_deferrals").update(updates).eq("id", id);
      if (error) throw error;
      if (role === "tm" && approved) {
        const deferral = deferrals.find((d: any) => d.id === id);
        if (deferral) await supabase.from("pms_jobs").update({ next_due_date: deferral.proposed_due_date, status: "scheduled", defer_approved_by: user?.id, defer_approved_by_name: fullName }).eq("id", deferral.job_id);
      }
      if (!approved) {
        const deferral = deferrals.find((d: any) => d.id === id);
        if (deferral) await supabase.from("pms_jobs").update({ status: "scheduled", defer_reason: null }).eq("id", deferral.job_id);
      }
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-deferrals"] }); queryClient.invalidateQueries({ queryKey: ["pms-jobs"] }); toast({ title: "Deferral decision recorded" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const addVesselAssignment = useMutation({
    mutationFn: async (form: any) => {
      const vessel = vessels.find((v: any) => v.id === form.vessel_id);
      const { error } = await supabase.from("pms_vessel_assignments").insert({ user_id: form.user_id, vessel_id: form.vessel_id, vessel_name: vessel?.name || "", pms_role: form.pms_role || "crew" });
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-vessel-assignments"] }); queryClient.invalidateQueries({ queryKey: ["pms-all-assignments"] }); queryClient.invalidateQueries({ queryKey: ["pms-crew-members"] }); toast({ title: "Assignment added" }); setAssignForm({}); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const assignJobToCrew = useMutation({
    mutationFn: async ({ jobId, userId, userName }: { jobId: string; userId: string; userName: string }) => {
      const { error } = await supabase.from("pms_jobs").update({ assigned_to: userId, assigned_to_name: userName }).eq("id", jobId);
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-jobs"] }); toast({ title: "Job assigned" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const removeAssignment = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("pms_vessel_assignments").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-vessel-assignments"] }); queryClient.invalidateQueries({ queryKey: ["pms-all-assignments"] }); queryClient.invalidateQueries({ queryKey: ["pms-crew-members"] }); toast({ title: "Assignment removed" }); },
  });

  // ─── Seed Vessel Mutation ──────────────────────────────────────────────
  const [seeding, setSeeding] = useState(false);
  const [seedProgress, setSeedProgress] = useState<{ current: number; total: number; vesselName: string } | null>(null);

  const seedSingleVessel = async (vesselId: string, vesselName: string) => {
    // Check if vessel already has equipment
    const { count } = await supabase.from("pms_equipment").select("*", { count: "exact", head: true }).eq("vessel_id", vesselId);
    if ((count || 0) > 0) return { vesselName, skipped: true, systemCount: 0, componentCount: 0 };

    // Phase 1: Insert system-level parent records
    const systemIdMap = new Map<string, string>();
    for (const sys of AIRCAT35_SYSTEMS) {
      const { data: sysRec, error: sysErr } = await supabase.from("pms_equipment").insert({
        vessel_id: vesselId, vessel_name: vesselName,
        equipment_name: sys.name, category: sys.category,
        system_name: sys.name, component_id_code: sys.id,
        criticality: "A", is_active: true,
      }).select("id").single();
      if (sysErr) throw sysErr;
      systemIdMap.set(sys.id, sysRec.id);
    }

    // Phase 2: Insert sub-systems and components
    const componentIdMap = new Map<string, string>();
    for (const sys of AIRCAT35_SYSTEMS) {
      const parentId = systemIdMap.get(sys.id)!;
      for (const sub of sys.subSystems) {
        const { data: subRec, error: subErr } = await supabase.from("pms_equipment").insert({
          vessel_id: vesselId, vessel_name: vesselName,
          equipment_name: sub.name, category: sys.category,
          system_name: sys.name, sub_system: sub.name,
          parent_equipment_id: parentId, criticality: "B", is_active: true,
        }).select("id").single();
        if (subErr) throw subErr;

        for (const comp of sub.components) {
          const { data: compRec, error: compErr } = await supabase.from("pms_equipment").insert({
            vessel_id: vesselId, vessel_name: vesselName,
            equipment_name: comp.name, category: sys.category,
            system_name: sys.name, sub_system: sub.name,
            parent_equipment_id: subRec.id,
            manufacturer: comp.make || null, model: comp.model || null,
            technical_specs: comp.specs || null, criticality: comp.criticality,
            location_on_vessel: comp.location || null,
            maintenance_source: comp.maintenanceSource || "Ops Manual",
            class_survey_item: comp.classSurveyItem || false,
            notes: comp.notes || null, is_active: true,
          }).select("id").single();
          if (compErr) throw compErr;
          componentIdMap.set(`${sys.name}||${comp.name}`, compRec.id);
        }
      }
    }

    // Phase 3: Insert maintenance job templates
    for (const tmpl of AIRCAT35_MAINTENANCE_TEMPLATES) {
      let equipmentId: string | null = null;
      if (tmpl.componentPattern) {
        for (const [key, id] of componentIdMap.entries()) {
          if (key.includes(tmpl.componentPattern)) { equipmentId = id; break; }
        }
      }
      if (!equipmentId) {
        const sys = AIRCAT35_SYSTEMS.find(s => s.name === tmpl.system);
        if (sys) equipmentId = systemIdMap.get(sys.id) || null;
      }
      if (!equipmentId) continue;

      await supabase.from("pms_jobs").insert({
        vessel_id: vesselId, vessel_name: vesselName,
        equipment_id: equipmentId, job_number: "",
        title: tmpl.title, job_type: tmpl.jobType,
        department: tmpl.department, priority: tmpl.priority,
        interval_days: tmpl.intervalDays || null,
        interval_hours: tmpl.intervalHours || null,
        class_requirement: tmpl.classRequirement || false,
        description: tmpl.description || null,
        status: "scheduled",
        next_due_date: tmpl.intervalDays
          ? new Date(Date.now() + tmpl.intervalDays * 86400000).toISOString().split("T")[0]
          : null,
      });
    }

    return { vesselName, skipped: false, systemCount: AIRCAT35_SYSTEMS.length, componentCount: componentIdMap.size };
  };

  const seedVessel = useMutation({
    mutationFn: async (vesselId: string) => {
      setSeeding(true);
      const vessel = vessels.find((v: any) => v.id === vesselId);
      if (!vessel) throw new Error("Vessel not found");
      return seedSingleVessel(vesselId, vessel.name);
    },
    onSuccess: (data) => {
      setSeeding(false);
      queryClient.invalidateQueries({ queryKey: ["pms-equipment"] });
      queryClient.invalidateQueries({ queryKey: ["pms-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["portal-pms-equipment"] });
      queryClient.invalidateQueries({ queryKey: ["portal-pms-jobs"] });
      if (data.skipped) {
        toast({ title: "Already initialized", description: `${data.vesselName} already has equipment records` });
      } else {
        toast({ title: "Vessel initialized", description: `${data.vesselName}: ${data.systemCount} systems, ${data.componentCount} components seeded` });
      }
    },
    onError: (e: any) => { setSeeding(false); toast({ title: "Seed failed", description: e.message, variant: "destructive" }); },
  });

  const seedAllVessels = useMutation({
    mutationFn: async () => {
      setSeeding(true);
      const results: { vesselName: string; skipped: boolean; componentCount: number }[] = [];
      const total = vessels.length;
      for (let i = 0; i < vessels.length; i++) {
        const v = vessels[i] as any;
        setSeedProgress({ current: i + 1, total, vesselName: v.name });
        try {
          const result = await seedSingleVessel(v.id, v.name);
          results.push(result);
        } catch (e: any) {
          console.error(`Failed to seed ${v.name}:`, e.message);
          results.push({ vesselName: v.name, skipped: true, componentCount: 0 });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      setSeeding(false);
      setSeedProgress(null);
      queryClient.invalidateQueries({ queryKey: ["pms-equipment"] });
      queryClient.invalidateQueries({ queryKey: ["pms-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["portal-pms-equipment"] });
      queryClient.invalidateQueries({ queryKey: ["portal-pms-jobs"] });
      const seeded = results.filter(r => !r.skipped);
      const skipped = results.filter(r => r.skipped);
      toast({
        title: "Fleet initialization complete",
        description: `${seeded.length} vessels seeded, ${skipped.length} already had data`,
      });
    },
    onError: (e: any) => { setSeeding(false); setSeedProgress(null); toast({ title: "Bulk seed failed", description: e.message, variant: "destructive" }); },
  });

  const addConditionReading = useMutation({
    mutationFn: async (form: any) => {
      const eq = equipment.find((e: any) => e.id === form.equipment_id);
      const vessel = vessels.find((v: any) => v.id === form.vessel_id);
      const paramType = CONDITION_PARAMETER_TYPES.find(p => p.value === form.parameter_type);
      let status = "normal";
      if (form.threshold_critical && form.reading_value >= form.threshold_critical) status = "critical";
      else if (form.threshold_warning && form.reading_value >= form.threshold_warning) status = "warning";
      const { error } = await supabase.from("pms_condition_monitoring").insert({
        ...form, vessel_name: vessel?.name || "", parameter_label: paramType?.label || form.parameter_type,
        unit: form.unit || paramType?.unit || "", status, recorded_by: user?.id, recorded_by_name: fullName,
      });
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-condition-monitoring"] }); toast({ title: "Condition reading logged" }); setShowAddCondition(false); setConditionForm({ parameter_type: "vibration" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // ─── Computed Stats ──────────────────────────────────────────────────────
  const overdueJobs = roleFilteredJobs.filter((j: any) => j.status === "overdue" || (j.status === "scheduled" && j.next_due_date && new Date(j.next_due_date) < new Date()));
  const scheduledJobs = roleFilteredJobs.filter((j: any) => j.status === "scheduled" && (!j.next_due_date || new Date(j.next_due_date) >= new Date()));
  const inProgressJobs = roleFilteredJobs.filter((j: any) => j.status === "in_progress");
  const completedJobs = roleFilteredJobs.filter((j: any) => j.status === "completed");
  const deferredJobs = roleFilteredJobs.filter((j: any) => j.status === "deferred");
  const openWOs = roleFilteredWOs.filter((w: any) => w.status === "open" || w.status === "in_progress" || w.status === "awaiting_parts");
  const lowStockParts = roleFilteredParts.filter((p: any) => p.quantity_on_hand <= p.reorder_point);
  const criticalEquipment = roleFilteredEquipment.filter((e: any) => e.criticality === "A");
  const completionRate = roleFilteredJobs.length > 0 ? Math.round((completedJobs.length / roleFilteredJobs.length) * 100) : 0;
  const pendingDeferrals = roleFilteredDeferrals.filter((d: any) => d.status === "pending" || d.status === "ce_approved");
  const criticalConditions = roleFilteredCondition.filter((c: any) => c.status === "critical");
  const warningConditions = roleFilteredCondition.filter((c: any) => c.status === "warning");
  const classJobs = roleFilteredJobs.filter((j: any) => j.class_requirement);

  const filterByVessel = (items: any[]) => {
    const filtered = items.filter((i: any) => !i.vessel_id || accessibleVesselIds.includes(i.vessel_id));
    return vesselFilter === "all" ? filtered : filtered.filter((i: any) => i.vessel_id === vesselFilter);
  };

  const filteredEquipment = filterByVessel(roleFilteredEquipment).filter((e: any) =>
    e.equipment_name.toLowerCase().includes(search.toLowerCase()) || e.equipment_code?.toLowerCase().includes(search.toLowerCase()) || e.category.toLowerCase().includes(search.toLowerCase())
  );
  const filteredJobs = filterByVessel(roleFilteredJobs).filter((j: any) => {
    const matchSearch = j.title.toLowerCase().includes(search.toLowerCase()) || j.job_number.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || j.status === statusFilter;
    return matchSearch && matchStatus;
  });
  const filteredWOs = filterByVessel(roleFilteredWOs).filter((w: any) => {
    const matchSearch = w.title.toLowerCase().includes(search.toLowerCase()) || w.work_order_number.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || w.status === statusFilter;
    return matchSearch && matchStatus;
  });
  const filteredParts = filterByVessel(roleFilteredParts).filter((p: any) =>
    p.description.toLowerCase().includes(search.toLowerCase()) || p.part_number.toLowerCase().includes(search.toLowerCase())
  );
  const vesselEquipment = (vesselId: string) => equipment.filter((e: any) => e.vessel_id === vesselId);

  // ─── Detail Panel Handler ───────────────────────────────────────────────
  const openDetail = useCallback((type: "equipment" | "job" | "work_order", item: any) => {
    setDetailType(type);
    setDetailItem(item);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailType(null);
    setDetailItem(null);
  }, []);

  const handleDetailAction = useCallback((action: string, data: any) => {
    switch (action) {
      case "selectEquipment": openDetail("equipment", data); break;
      case "selectJob": openDetail("job", data); break;
      case "selectWO": openDetail("work_order", data); break;
      case "createJob":
        setJobForm({ ...jobForm, vessel_id: data.vessel_id, equipment_id: data.id });
        setShowAddJob(true); break;
      case "createWO":
        setWoForm({ ...woForm, vessel_id: data.vessel_id, equipment_id: data.equipment_id || data.id });
        setShowAddWO(true); break;
      case "logReading":
        setConditionForm({ ...conditionForm, vessel_id: data.vessel_id, equipment_id: data.id });
        setShowAddCondition(true); break;
      case "logHours":
        setHoursForm({ vessel_id: data.vessel_id, equipment_id: data.id });
        setShowLogHours(true); break;
      case "startJob":
        updateJobStatus.mutate({ id: data.id, updates: { status: "in_progress" } }); closeDetail(); break;
      case "completeJob":
        setCompleteJob(data); setShowCompleteDialog(true); closeDetail(); break;
      case "deferJob":
        setDeferJob(data); setShowDeferDialog(true); break;
      case "startWO":
        updateWOStatus.mutate({ id: data.id, updates: { status: "in_progress", started_at: new Date().toISOString() } }); closeDetail(); break;
      case "completeWO":
        updateWOStatus.mutate({ id: data.id, updates: { status: "completed", completed_at: new Date().toISOString() } }); closeDetail(); break;
      case "awaitPartsWO":
        updateWOStatus.mutate({ id: data.id, updates: { status: "awaiting_parts" } }); closeDetail(); break;
      case "closeWO":
        updateWOStatus.mutate({ id: data.id, updates: { status: "closed", closed_at: new Date().toISOString(), closed_by: user?.id, closed_by_name: fullName } }); closeDetail(); break;
      case "approveCE":
        updateWOStatus.mutate({ id: data.id, updates: { chief_engineer_approved: true, chief_engineer_id: user?.id, chief_engineer_name: fullName, chief_engineer_approved_at: new Date().toISOString() } }); closeDetail(); break;
      case "approveSupt":
        updateWOStatus.mutate({ id: data.id, updates: { superintendent_approved: true, superintendent_id: user?.id, superintendent_name: fullName, superintendent_approved_at: new Date().toISOString() } }); closeDetail(); break;
      case "approveJSA":
        updateWOStatus.mutate({ id: data.id, updates: { jsa_approved_by: user?.id, jsa_approved_by_name: fullName, jsa_approved_at: new Date().toISOString() } }); closeDetail(); break;
      case "createPO":
        createPOFromWO.mutate(data); closeDetail(); break;
    }
  }, [openDetail, closeDetail, updateJobStatus, updateWOStatus, createPOFromWO, user?.id, fullName, jobForm, woForm, conditionForm]);

  const handleSectionChange = useCallback((s: string) => {
    setActiveSection(s);
    setSearch("");
    setStatusFilter("all");
    closeDetail();
  }, [closeDetail]);

  // ═════════════════════════════════════════════════════════════════════════
  // ─── DASHBOARD ──────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Quick Action Bar */}
      <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg border">
        <Button size="sm" onClick={() => setShowAddEquipment(true)} className="bg-teal hover:bg-teal-light text-white text-xs"><Cog className="w-3.5 h-3.5 mr-1" />Register Equipment</Button>
        <Button size="sm" onClick={() => setShowAddJob(true)} variant="outline" className="text-xs"><ClipboardList className="w-3.5 h-3.5 mr-1" />Create Job</Button>
        <Button size="sm" onClick={() => setShowAddWO(true)} variant="outline" className="text-xs"><FileText className="w-3.5 h-3.5 mr-1" />New Work Order</Button>
        <Button size="sm" onClick={() => setShowLogHours(true)} variant="outline" className="text-xs"><Gauge className="w-3.5 h-3.5 mr-1" />Log Hours</Button>
        <Button size="sm" onClick={() => setShowAddCondition(true)} variant="outline" className="text-xs"><Activity className="w-3.5 h-3.5 mr-1" />Log Reading</Button>
        <Button size="sm" onClick={() => setShowAddPart(true)} variant="outline" className="text-xs"><Package className="w-3.5 h-3.5 mr-1" />Add Part</Button>
        {isTechnicalManager && (
          <Button size="sm" variant="outline" className="text-xs border-teal text-teal hover:bg-teal/10 ml-auto" disabled={seeding}
            onClick={() => { if (confirm(`Initialize ALL ${vessels.length} fleet vessels with the AIRCAT 35 component tree? Vessels already seeded will be skipped.`)) seedAllVessels.mutate(); }}>
            <Rocket className="w-3.5 h-3.5 mr-1" />{seeding ? (seedProgress ? `Seeding ${seedProgress.current}/${seedProgress.total}: ${seedProgress.vesselName}...` : "Seeding...") : `Seed All Vessels (${vessels.length})`}
          </Button>
        )}
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {[
          { label: "Equipment", value: roleFilteredEquipment.length, icon: Cog, color: "text-foreground", section: "equipment" },
          { label: "Overdue", value: overdueJobs.length, icon: AlertTriangle, color: overdueJobs.length > 0 ? "text-red-500" : "text-emerald-500", section: "jobs" },
          { label: "In Progress", value: inProgressJobs.length, icon: Wrench, color: "text-amber-500", section: "jobs" },
          { label: "Open WOs", value: openWOs.length, icon: FileText, color: "text-teal", section: "work_orders" },
          { label: "Low Stock", value: lowStockParts.length, icon: Package, color: lowStockParts.length > 0 ? "text-orange-500" : "text-emerald-500", section: "spare_parts" },
        ].map(k => (
          <Card key={k.label} className="cursor-pointer hover:shadow-md transition-shadow group" onClick={() => handleSectionChange(k.section)}>
            <CardContent className="p-3 flex items-center gap-2">
              <k.icon className={`w-5 h-5 ${k.color} shrink-0`} />
              <div className="min-w-0">
                <p className="text-lg font-display text-foreground">{k.value}</p>
                <p className="text-[9px] text-muted-foreground font-body truncate">{k.label}</p>
              </div>
              <ChevronRight className="w-3 h-3 text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Completion Rate + Class Compliance */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardContent className="p-4"><div className="flex items-center justify-between mb-2"><span className="text-sm font-display">PMS Completion Rate</span><span className="text-sm font-bold text-teal">{completionRate}%</span></div><Progress value={completionRate} className="h-3" /></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center justify-between mb-2"><span className="text-sm font-display">Class/Statutory Jobs</span><span className="text-sm font-bold text-foreground">{classJobs.length}</span></div>
          <div className="flex gap-4 text-xs">
            <span className="text-emerald-600">{classJobs.filter((j: any) => j.status === "completed").length} completed</span>
            <span className="text-red-500">{classJobs.filter((j: any) => j.status === "overdue" || (j.status === "scheduled" && j.next_due_date && new Date(j.next_due_date) < new Date())).length} overdue</span>
            <span className="text-blue-600">{classJobs.filter((j: any) => j.status === "scheduled").length} scheduled</span>
          </div>
        </CardContent></Card>
      </div>

      {/* Alert Cards - Clickable */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="border-red-200"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-red-600"><AlertTriangle className="w-4 h-4" />Overdue ({overdueJobs.length})</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 max-h-52 overflow-y-auto">
            {overdueJobs.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No overdue jobs ✓</p> : overdueJobs.slice(0, 10).map((j: any) => (
              <button key={j.id} className="w-full flex items-center justify-between p-2 rounded bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors text-left"
                onClick={() => openDetail("job", j)}>
                <div className="min-w-0"><p className="text-xs font-display truncate">{j.title}</p><p className="text-[10px] text-muted-foreground">{j.job_number} · {j.vessel_name}</p></div>
                <div className="flex items-center gap-1.5 shrink-0"><PriorityBadge priority={j.priority} /><ChevronRight className="w-3 h-3 text-muted-foreground" /></div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="border-amber-200"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-amber-600"><Activity className="w-4 h-4" />Condition Alerts ({criticalConditions.length + warningConditions.length})</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 max-h-52 overflow-y-auto">
            {criticalConditions.length + warningConditions.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">All readings normal ✓</p> : [...criticalConditions, ...warningConditions].slice(0, 10).map((c: any) => {
              const eq = equipment.find((e: any) => e.id === c.equipment_id);
              return (
                <button key={c.id} className={`w-full flex items-center justify-between p-2 rounded text-left transition-colors ${c.status === "critical" ? "bg-red-50 dark:bg-red-500/10 hover:bg-red-100" : "bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100"}`}
                  onClick={() => eq && openDetail("equipment", eq)}>
                  <div className="min-w-0"><p className="text-xs font-display truncate">{eq?.equipment_name || "—"}</p><p className="text-[10px] text-muted-foreground">{c.parameter_label} · {c.vessel_name}</p></div>
                  <div className="text-right shrink-0"><p className={`text-sm font-bold ${c.status === "critical" ? "text-red-600" : "text-amber-600"}`}>{c.reading_value} {c.unit}</p></div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-orange-200"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-orange-600"><Package className="w-4 h-4" />Low Stock ({lowStockParts.length})</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 max-h-52 overflow-y-auto">
            {lowStockParts.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">Stock adequate ✓</p> : lowStockParts.slice(0, 10).map((p: any) => (
              <div key={p.id} className="flex items-center justify-between p-2 rounded bg-orange-50 dark:bg-orange-500/10">
                <div className="min-w-0"><p className="text-xs font-display truncate">{p.description}</p><p className="text-[10px] text-muted-foreground">{p.part_number} · {p.vessel_name || "Shore"}</p></div>
                <div className="flex items-center gap-2 shrink-0">
                  <p className="text-xs font-bold text-orange-600">{p.quantity_on_hand}/{p.minimum_quantity}</p>
                  <Button size="sm" variant="ghost" className="h-5 text-[9px] text-teal" onClick={() => autoReorderPart.mutate(p)}><ShoppingCart className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // ─── COMPONENT TREE ─────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderComponentTree = () => {
    const treeVessel = selectedTreeVessel || (accessibleVessels.length > 0 ? accessibleVessels[0].id : null);
    const vesselData = vessels.find((v: any) => v.id === treeVessel);
    const vesselEq = roleFilteredEquipment.filter((e: any) => e.vessel_id === treeVessel);
    const vesselJbs = roleFilteredJobs.filter((j: any) => j.vessel_id === treeVessel);
    const vesselWOs = roleFilteredWOs.filter((w: any) => w.vessel_id === treeVessel);

    // Group by system_name (19-system hierarchy)
    const systemGrouped = new Map<string, any[]>();
    vesselEq.forEach((e: any) => {
      const sysName = e.system_name || e.category || "Uncategorised";
      if (!systemGrouped.has(sysName)) systemGrouped.set(sysName, []);
      systemGrouped.get(sysName)!.push(e);
    });

    const toggleCategory = (cat: string) => setExpandedCategories(prev => { const n = new Set(prev); if (n.has(cat)) n.delete(cat); else n.add(cat); return n; });
    const toggleEquipment = (eqId: string) => setExpandedEquipment(prev => { const n = new Set(prev); if (n.has(eqId)) n.delete(eqId); else n.add(eqId); return n; });

    const vOverdue = vesselJbs.filter((j: any) => j.status === "overdue" || (j.status === "scheduled" && j.next_due_date && new Date(j.next_due_date) < new Date())).length;
    const vCompleted = vesselJbs.filter((j: any) => j.status === "completed").length;
    const vCompletionRate = vesselJbs.length > 0 ? Math.round((vCompleted / vesselJbs.length) * 100) : 0;

    const renderEquipmentNode = (eq: any, depth: number = 0) => {
      const eqJobs = vesselJbs.filter((j: any) => j.equipment_id === eq.id);
      const eqWOs = vesselWOs.filter((w: any) => w.equipment_id === eq.id);
      const childEquipment = vesselEq.filter((e: any) => e.parent_equipment_id === eq.id);
      const isExpanded = expandedEquipment.has(eq.id);
      const eqOverdue = eqJobs.filter((j: any) => j.status === "overdue" || (j.status === "scheduled" && j.next_due_date && new Date(j.next_due_date) < new Date())).length;
      const isSelected = detailType === "equipment" && detailItem?.id === eq.id;

      return (
        <div key={eq.id} className={`border-l-2 ${depth > 0 ? "border-muted/50 ml-4" : "border-muted ml-2"}`}>
          <div className={`flex items-center gap-1 hover:bg-muted/30 transition-colors ${isSelected ? "bg-teal/10 border-r-2 border-teal" : ""}`}>
            <button className="p-1.5 pl-3" onClick={() => toggleEquipment(eq.id)}>
              {(childEquipment.length > 0 || eqJobs.length > 0 || eqWOs.length > 0)
                ? (isExpanded ? <ChevronDown className="w-3 h-3 text-muted-foreground" /> : <ChevronRight className="w-3 h-3 text-muted-foreground" />)
                : <CircleDot className="w-3 h-3 text-muted-foreground/30" />}
            </button>
            <button className="flex-1 flex items-center gap-2 p-1.5 text-left min-w-0" onClick={() => openDetail("equipment", eq)}>
              <Wrench className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-display truncate">{eq.equipment_name}</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {eq.component_id_code || eq.equipment_code || "—"}
                  {eq.manufacturer ? ` · ${eq.manufacturer}` : ""}
                  {eq.model ? ` ${eq.model}` : ""}
                </p>
              </div>
              <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold shrink-0 ${CRITICALITY.find(c => c.value === eq.criticality)?.color || ""}`}>{eq.criticality}</span>
              {eq.class_survey_item && <Shield className="w-3 h-3 text-blue-500 shrink-0" />}
              {eqJobs.length > 0 && <Badge variant="outline" className="text-[8px] shrink-0">{eqJobs.length}</Badge>}
              {eqOverdue > 0 && <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />}
            </button>
          </div>

          {isExpanded && (
            <div className="pl-4">
              {eqJobs.map((job: any) => (
                <button key={job.id} className={`w-full flex items-center gap-2 p-2 pl-6 text-left hover:bg-muted/30 transition-colors ${detailType === "job" && detailItem?.id === job.id ? "bg-teal/10" : ""}`}
                  onClick={() => openDetail("job", job)}>
                  <ClipboardList className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] truncate flex-1">{job.title}</span>
                  <StatusBadge status={job.status} config={JOB_STATUS_CONFIG} />
                </button>
              ))}
              {eqWOs.map((wo: any) => (
                <button key={wo.id} className={`w-full flex items-center gap-2 p-2 pl-6 text-left hover:bg-muted/30 transition-colors ${detailType === "work_order" && detailItem?.id === wo.id ? "bg-teal/10" : ""}`}
                  onClick={() => openDetail("work_order", wo)}>
                  <FileText className="w-3 h-3 text-muted-foreground" />
                  <span className="text-[10px] truncate flex-1">{wo.work_order_number}: {wo.title}</span>
                  <StatusBadge status={wo.status} config={WO_STATUS_CONFIG} />
                </button>
              ))}
              {childEquipment.map((child: any) => renderEquipmentNode(child, depth + 1))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={treeVessel || ""} onValueChange={v => { setSelectedTreeVessel(v); setExpandedCategories(new Set()); setExpandedEquipment(new Set()); closeDetail(); }}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Select vessel" /></SelectTrigger>
            <SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
          </Select>
          {vesselData && <Badge variant="outline" className="text-xs capitalize">{vesselData.status?.replace("_", " ")}</Badge>}
          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <span>{vesselEq.length} assets</span>
            <span>·</span>
            <span>{vesselJbs.length} jobs</span>
            {vOverdue > 0 && <><span>·</span><span className="text-red-500 font-bold">{vOverdue} overdue</span></>}
          </div>
        </div>

        {treeVessel && vesselData ? (
          <>
            <Card><CardContent className="p-3"><div className="flex items-center justify-between mb-1"><span className="text-xs font-display">PMS Completion — {vesselData.name}</span><span className="text-xs font-bold text-teal">{vCompletionRate}%</span></div><Progress value={vCompletionRate} className="h-2" /></CardContent></Card>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FolderTree className="w-4 h-4 text-teal" />AIRCAT 35 Equipment Hierarchy
                    <span className="text-[10px] text-muted-foreground font-normal ml-2">System → Sub-System → Component</span>
                  </CardTitle>
                  {vesselEq.length === 0 && isTechnicalManager && (
                    <Button size="sm" className="bg-teal hover:bg-teal-light text-white text-xs" disabled={seeding}
                      onClick={() => { if (treeVessel && confirm(`Initialize ${vesselData.name} with the full AIRCAT 35 component tree? This will seed ~200 components and ~35 maintenance jobs.`)) seedVessel.mutate(treeVessel); }}>
                      <Rocket className="w-3.5 h-3.5 mr-1" />{seeding ? "Seeding..." : "Initialize Vessel"}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-1 p-3">
                {systemGrouped.size === 0 ? (
                  <div className="text-center py-8">
                    <Database className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No equipment registered for this vessel</p>
                    <p className="text-xs text-muted-foreground mt-1">Use "Initialize Vessel" to seed the AIRCAT 35 component tree</p>
                    {!isTechnicalManager && (
                      <Button size="sm" className="mt-3 bg-teal hover:bg-teal-light text-white" onClick={() => setShowAddEquipment(true)}>
                        <Plus className="w-3.5 h-3.5 mr-1" />Register First Asset
                      </Button>
                    )}
                  </div>
                ) : (
                  // Render by 19-system grouping
                  AIRCAT35_SYSTEMS.map(sys => {
                    const sysEq = systemGrouped.get(sys.name);
                    if (!sysEq || sysEq.length === 0) return null;
                    const rootEq = sysEq.filter((e: any) => !e.parent_equipment_id || !sysEq.some((p: any) => p.id === e.parent_equipment_id));
                    const sysOverdue = sysEq.reduce((sum: number, eq: any) => sum + vesselJbs.filter((j: any) => j.equipment_id === eq.id && (j.status === "overdue" || (j.status === "scheduled" && j.next_due_date && new Date(j.next_due_date) < new Date()))).length, 0);
                    const sysClassItems = sysEq.filter((e: any) => e.class_survey_item).length;
                    const isCatExpanded = expandedCategories.has(sys.id);

                    return (
                      <div key={sys.id} className="border rounded-lg overflow-hidden">
                        <button className="w-full flex items-center gap-2 p-3 hover:bg-muted/50 transition-colors text-left" onClick={() => toggleCategory(sys.id)}>
                          {isCatExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                          <span className="text-sm">{sys.icon}</span>
                          <span className="text-sm font-display flex-1">{sys.name}</span>
                          <Badge variant="outline" className="text-[9px]">{sysEq.length}</Badge>
                          {sysClassItems > 0 && <Badge variant="outline" className="text-[8px] border-blue-300 text-blue-600"><Shield className="w-2.5 h-2.5 mr-0.5" />{sysClassItems}</Badge>}
                          {sysOverdue > 0 && <Badge className="text-[8px] bg-red-100 text-red-700">{sysOverdue} overdue</Badge>}
                        </button>
                        {isCatExpanded && (
                          <div className="pb-2 border-t border-muted">
                            {rootEq.map((eq: any) => renderEquipmentNode(eq))}
                          </div>
                        )}
                      </div>
                    );
                  }).filter(Boolean)
                )}

                {/* Also render any equipment not matching a known system */}
                {Array.from(systemGrouped.entries())
                  .filter(([sysName]) => !AIRCAT35_SYSTEMS.some(s => s.name === sysName))
                  .map(([sysName, eqList]) => {
                    const rootEq = eqList.filter((e: any) => !e.parent_equipment_id || !eqList.some((p: any) => p.id === e.parent_equipment_id));
                    const isCatExpanded = expandedCategories.has(sysName);
                    return (
                      <div key={sysName} className="border rounded-lg overflow-hidden">
                        <button className="w-full flex items-center gap-2 p-3 hover:bg-muted/50 transition-colors text-left" onClick={() => toggleCategory(sysName)}>
                          {isCatExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                          <span className="text-sm">⚙️</span>
                          <span className="text-sm font-display flex-1">{sysName}</span>
                          <Badge variant="outline" className="text-[9px]">{eqList.length}</Badge>
                        </button>
                        {isCatExpanded && (
                          <div className="pb-2 border-t border-muted">
                            {rootEq.map((eq: any) => renderEquipmentNode(eq))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </CardContent>
            </Card>
          </>
        ) : (
          <Card><CardContent className="p-12 text-center text-muted-foreground"><Ship className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />Select a vessel to view its component tree</CardContent></Card>
        )}
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── EQUIPMENT REGISTRY ─────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderEquipment = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search equipment..." className="pl-10" /></div>
        <Button onClick={() => setShowAddEquipment(true)} size="sm" className="bg-teal hover:bg-teal-light text-white"><Plus className="w-4 h-4 mr-1" />Add Equipment</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Assets", value: roleFilteredEquipment.length },
          { label: "Class A (Critical)", value: criticalEquipment.length, color: "text-red-500" },
          { label: "Class B (Essential)", value: roleFilteredEquipment.filter((e: any) => e.criticality === "B").length, color: "text-amber-500" },
          { label: "Class C (Standard)", value: roleFilteredEquipment.filter((e: any) => e.criticality === "C").length, color: "text-blue-500" },
        ].map(k => (
          <Card key={k.label}><CardContent className="p-3 text-center"><p className={`text-xl font-bold ${(k as any).color || "text-foreground"}`}>{k.value}</p><p className="text-[10px] text-muted-foreground">{k.label}</p></CardContent></Card>
        ))}
      </div>

      {loadingEquipment ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div> : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead><TableHead>Equipment</TableHead><TableHead>Category</TableHead><TableHead>Vessel</TableHead>
              <TableHead>Criticality</TableHead><TableHead>Manufacturer</TableHead><TableHead>Location</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredEquipment.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No equipment found</TableCell></TableRow>
              ) : filteredEquipment.map((eq: any) => (
                <TableRow key={eq.id} className={`cursor-pointer hover:bg-muted/50 transition-colors ${detailType === "equipment" && detailItem?.id === eq.id ? "bg-teal/5" : ""}`}
                  onClick={() => openDetail("equipment", eq)}>
                  <TableCell className="font-mono text-xs">{eq.equipment_code || "—"}</TableCell>
                  <TableCell><p className="text-sm font-display">{eq.equipment_name}</p><p className="text-[10px] text-muted-foreground">{eq.sub_system || ""}</p></TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{EQUIPMENT_CATEGORIES.find(c => c.value === eq.category)?.label || eq.category}</Badge></TableCell>
                  <TableCell className="text-xs">{eq.vessel_name}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CRITICALITY.find(c => c.value === eq.criticality)?.color || ""}`}>{eq.criticality}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{eq.manufacturer || "—"}{eq.model ? ` / ${eq.model}` : ""}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{eq.location_on_vessel || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // ─── PLANNED JOBS ───────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderJobs = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs..." className="pl-10" /></div>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{Object.entries(JOB_STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
        <Button onClick={() => setShowAddJob(true)} size="sm" className="bg-teal hover:bg-teal-light text-white"><Plus className="w-4 h-4 mr-1" />Create Job</Button>
      </div>

      {loadingJobs ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div> : (
        <div className="space-y-1.5">
          {filteredJobs.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><Wrench className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">No jobs found</p></CardContent></Card>
          ) : filteredJobs.map((job: any) => {
            const isSelected = detailType === "job" && detailItem?.id === job.id;
            return (
              <button key={job.id} className={`w-full text-left rounded-lg border p-3 hover:shadow-md transition-all ${isSelected ? "border-teal bg-teal/5 shadow-md" : "border-border hover:border-muted-foreground/20"}`}
                onClick={() => openDetail("job", job)}>
                <div className="flex items-center gap-3">
                  <Wrench className={`w-5 h-5 shrink-0 ${job.priority === "critical" ? "text-red-600" : job.priority === "high" ? "text-orange-500" : "text-muted-foreground"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display text-sm text-foreground">{job.title}</p>
                      {job.class_requirement && <Badge variant="outline" className="text-[8px]">CLASS</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground font-body mt-0.5">
                      {job.job_number} · {job.vessel_name} · {job.department} · {JOB_TYPES.find(t => t.value === job.job_type)?.label}
                      {job.interval_days ? ` · Every ${job.interval_days}d` : ""}{job.interval_hours ? ` · Every ${job.interval_hours}hr` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={job.status} config={JOB_STATUS_CONFIG} />
                    <PriorityBadge priority={job.priority} />
                    {job.next_due_date && <span className="text-[10px] text-muted-foreground">{format(new Date(job.next_due_date), "dd MMM")}</span>}
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // ─── WORK ORDERS ────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderWorkOrders = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search work orders..." className="pl-10" /></div>
        <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{Object.entries(WO_STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
        <Button onClick={() => setShowAddWO(true)} size="sm" className="bg-teal hover:bg-teal-light text-white"><Plus className="w-4 h-4 mr-1" />Create Work Order</Button>
      </div>

      {loadingWO ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div> : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>WO #</TableHead><TableHead>Title</TableHead><TableHead>Vessel</TableHead><TableHead>Type</TableHead>
              <TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>CE</TableHead><TableHead>PO</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredWOs.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No work orders found</TableCell></TableRow>
              ) : filteredWOs.map((wo: any) => {
                const isSelected = detailType === "work_order" && detailItem?.id === wo.id;
                return (
                  <TableRow key={wo.id} className={`cursor-pointer hover:bg-muted/50 transition-colors ${isSelected ? "bg-teal/5" : ""}`} onClick={() => openDetail("work_order", wo)}>
                    <TableCell className="font-mono text-xs">{wo.work_order_number}</TableCell>
                    <TableCell><p className="text-sm font-display">{wo.title}</p><p className="text-[10px] text-muted-foreground">{wo.equipment_name || "—"}</p></TableCell>
                    <TableCell className="text-xs">{wo.vessel_name}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{wo.work_type}</Badge></TableCell>
                    <TableCell><PriorityBadge priority={wo.priority} /></TableCell>
                    <TableCell><StatusBadge status={wo.status} config={WO_STATUS_CONFIG} /></TableCell>
                    <TableCell>{wo.chief_engineer_approved ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-muted-foreground" />}</TableCell>
                    <TableCell>{wo.linked_po_number ? <Badge variant="outline" className="text-[10px] text-teal">{wo.linked_po_number}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // ─── CONDITION MONITORING ───────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderConditionMonitoring = () => {
    const filteredCond = filterByVessel(roleFilteredCondition);
    const latestMap = new Map<string, any>();
    filteredCond.forEach((c: any) => {
      const key = `${c.equipment_id}-${c.parameter_type}`;
      if (!latestMap.has(key)) latestMap.set(key, c);
    });
    const latestReadings = Array.from(latestMap.values());

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-teal" />
            <span className="text-sm font-display">Condition-Based Maintenance (CBM)</span>
          </div>
          <Button onClick={() => setShowAddCondition(true)} size="sm" className="bg-teal hover:bg-teal-light text-white"><Plus className="w-4 h-4 mr-1" />Log Reading</Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Total Parameters", value: latestReadings.length, color: "text-foreground" },
            { label: "Normal", value: latestReadings.filter(r => r.status === "normal").length, color: "text-emerald-500" },
            { label: "Warning", value: latestReadings.filter(r => r.status === "warning").length, color: "text-amber-500" },
            { label: "Critical", value: latestReadings.filter(r => r.status === "critical").length, color: "text-red-500" },
          ].map(k => (
            <Card key={k.label}><CardContent className="p-3 text-center"><p className={`text-xl font-bold ${k.color}`}>{k.value}</p><p className="text-[10px] text-muted-foreground">{k.label}</p></CardContent></Card>
          ))}
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Equipment</TableHead><TableHead>Vessel</TableHead><TableHead>Parameter</TableHead><TableHead>Reading</TableHead>
              <TableHead>Threshold</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {latestReadings.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No condition data recorded</TableCell></TableRow>
              ) : latestReadings.map((c: any) => {
                const eq = equipment.find((e: any) => e.id === c.equipment_id);
                return (
                  <TableRow key={c.id} className={`cursor-pointer hover:bg-muted/50 ${c.status === "critical" ? "bg-red-50/50 dark:bg-red-500/5" : c.status === "warning" ? "bg-amber-50/50 dark:bg-amber-500/5" : ""}`}
                    onClick={() => eq && openDetail("equipment", eq)}>
                    <TableCell><p className="text-sm font-display">{eq?.equipment_name || "—"}</p></TableCell>
                    <TableCell className="text-xs">{c.vessel_name}</TableCell>
                    <TableCell className="text-xs">{c.parameter_label}</TableCell>
                    <TableCell className={`text-sm font-bold ${c.status === "critical" ? "text-red-600" : c.status === "warning" ? "text-amber-600" : "text-emerald-600"}`}>{c.reading_value} {c.unit}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.threshold_warning ? `W: ${c.threshold_warning}` : ""} {c.threshold_critical ? `C: ${c.threshold_critical}` : ""}</TableCell>
                    <TableCell><Badge className={`text-[8px] ${c.status === "critical" ? "bg-red-500" : c.status === "warning" ? "bg-amber-500" : "bg-emerald-500"}`}>{c.status.toUpperCase()}</Badge></TableCell>
                    <TableCell className="text-xs">{format(new Date(c.recorded_date), "dd MMM yy")}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── RUNNING HOURS ──────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderRunningHours = () => {
    const filteredHours = filterByVessel(runningHours.filter((h: any) => accessibleVesselIds.includes(h.vessel_id)));
    const latestByEquipment = new Map<string, any>();
    filteredHours.forEach((h: any) => { if (!latestByEquipment.has(h.equipment_id)) latestByEquipment.set(h.equipment_id, h); });

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Gauge className="w-4 h-4 text-teal" /><span className="text-sm font-display">Running Hours</span></div>
          <Button onClick={() => setShowLogHours(true)} size="sm" className="bg-teal hover:bg-teal-light text-white"><Plus className="w-4 h-4 mr-1" />Log Hours</Button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from(latestByEquipment.entries()).map(([eqId, h]) => {
            const eq = equipment.find((e: any) => e.id === eqId);
            return (
              <Card key={eqId} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => eq && openDetail("equipment", eq)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="min-w-0"><p className="text-sm font-display truncate">{eq?.equipment_name || "—"}</p><p className="text-[10px] text-muted-foreground">{eq?.vessel_name} · {eq?.equipment_code || ""}</p></div>
                    <div className="text-right shrink-0"><p className="text-lg font-bold text-teal">{h.hours_reading.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">hours</p></div>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Last recorded: {format(new Date(h.recorded_date), "dd MMM yyyy")} by {h.recorded_by_name || "—"}</p>
                </CardContent>
              </Card>
            );
          })}
          {latestByEquipment.size === 0 && <Card className="md:col-span-3"><CardContent className="p-8 text-center text-muted-foreground text-sm">No running hours recorded</CardContent></Card>}
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── SPARE PARTS ────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderSpareParts = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search parts..." className="pl-10" /></div>
        <Button onClick={() => setShowAddPart(true)} size="sm" className="bg-teal hover:bg-teal-light text-white"><Plus className="w-4 h-4 mr-1" />Add Part</Button>
        {lowStockParts.length > 0 && (
          <Button size="sm" variant="outline" className="text-orange-600 border-orange-300" onClick={() => lowStockParts.forEach(p => autoReorderPart.mutate(p))}>
            <ShoppingCart className="w-4 h-4 mr-1" />Auto-Reorder All ({lowStockParts.length})
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total Parts", value: roleFilteredParts.length },
          { label: "Below Min", value: roleFilteredParts.filter((p: any) => p.quantity_on_hand < p.minimum_quantity).length, color: "text-red-500" },
          { label: "At Reorder", value: lowStockParts.length, color: "text-orange-500" },
          { label: "Critical Spares", value: roleFilteredParts.filter((p: any) => p.is_critical_spare).length, color: "text-red-600" },
          { label: "Over Max", value: roleFilteredParts.filter((p: any) => p.quantity_on_hand > p.maximum_quantity).length, color: "text-purple-500" },
        ].map(k => (
          <Card key={k.label}><CardContent className="p-3 text-center"><p className={`text-xl font-bold ${(k as any).color || "text-foreground"}`}>{k.value}</p><p className="text-[10px] text-muted-foreground">{k.label}</p></CardContent></Card>
        ))}
      </div>

      {loadingParts ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div> : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Part #</TableHead><TableHead>Description</TableHead><TableHead>Vessel</TableHead>
              <TableHead className="text-center">Qty</TableHead><TableHead className="text-center">Min</TableHead><TableHead className="text-center">Max</TableHead>
              <TableHead>Status</TableHead><TableHead>Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredParts.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No parts found</TableCell></TableRow>
              ) : filteredParts.map((p: any) => {
                const belowMin = p.quantity_on_hand < p.minimum_quantity;
                const atReorder = p.quantity_on_hand <= p.reorder_point;
                return (
                  <TableRow key={p.id} className={belowMin ? "bg-red-50/50 dark:bg-red-500/5" : atReorder ? "bg-orange-50/50 dark:bg-orange-500/5" : ""}>
                    <TableCell className="font-mono text-xs">{p.part_number}</TableCell>
                    <TableCell><p className="text-sm">{p.description}</p>{p.is_critical_spare && <Badge variant="destructive" className="text-[8px] mt-0.5">CRITICAL</Badge>}</TableCell>
                    <TableCell className="text-xs">{p.vessel_name || "Shore"}</TableCell>
                    <TableCell className={`text-center font-bold ${belowMin ? "text-red-600" : atReorder ? "text-orange-600" : "text-foreground"}`}>{p.quantity_on_hand}</TableCell>
                    <TableCell className="text-center text-xs text-muted-foreground">{p.minimum_quantity}</TableCell>
                    <TableCell className="text-center text-xs text-muted-foreground">{p.maximum_quantity}</TableCell>
                    <TableCell>
                      {belowMin ? <Badge variant="destructive" className="text-[8px]">BELOW MIN</Badge>
                        : atReorder ? <Badge className="text-[8px] bg-orange-500">REORDER</Badge>
                        : <Badge variant="outline" className="text-[8px] text-emerald-600">OK</Badge>}
                    </TableCell>
                    <TableCell>
                      {(belowMin || atReorder) && (
                        <Button size="sm" variant="ghost" className="h-6 text-[9px] text-teal" onClick={() => autoReorderPart.mutate(p)}><ShoppingCart className="w-3 h-3 mr-1" />Reorder</Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // ─── DEFERRALS ──────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderDeferrals = () => (
    <div className="space-y-4">
      {pendingDeferrals.length > 0 && (isChiefEngineer || isTechnicalManager) && (
        <Card className="border-purple-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-purple-700"><PauseCircle className="w-4 h-4" />Pending Approvals ({pendingDeferrals.length})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {pendingDeferrals.map((d: any) => {
              const canCEApprove = isChiefEngineer && d.status === "pending" && !d.ce_approved;
              const canTMApprove = isTechnicalManager && d.status === "ce_approved" && d.ce_approved && !d.tm_approved;
              return (
                <div key={d.id} className="p-4 rounded-lg border border-purple-100 bg-purple-50/50 dark:bg-purple-500/5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div><p className="text-sm font-display">{d.job_title}</p><p className="text-xs text-muted-foreground">{d.job_number} · {d.vessel_name} · By {d.requested_by_name}</p></div>
                    <StatusBadge status={d.status} config={DEFERRAL_STATUS_CONFIG} />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div><span className="text-muted-foreground">Original:</span> <span className="font-medium">{d.original_due_date || "—"}</span></div>
                    <div><span className="text-muted-foreground">Proposed:</span> <span className="font-medium text-purple-700">{d.proposed_due_date}</span></div>
                    <div className="md:col-span-2"><span className="text-muted-foreground">Reason:</span> <span className="font-medium">{d.reason}</span></div>
                  </div>
                  {d.risk_assessment && <div className="text-xs"><span className="text-muted-foreground">Risk:</span> {d.risk_assessment}</div>}
                  <div className="flex gap-2">
                    {canCEApprove && <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => approveDeferral.mutate({ id: d.id, role: "ce", approved: true })}>CE Approve</Button>
                      <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={() => approveDeferral.mutate({ id: d.id, role: "ce", approved: false })}>Reject</Button>
                    </>}
                    {canTMApprove && <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => approveDeferral.mutate({ id: d.id, role: "tm", approved: true })}>TM Approve</Button>
                      <Button size="sm" variant="outline" className="text-xs text-red-600" onClick={() => approveDeferral.mutate({ id: d.id, role: "tm", approved: false })}>Reject</Button>
                    </>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* All deferrals list */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Job</TableHead><TableHead>Vessel</TableHead><TableHead>Reason</TableHead>
            <TableHead>Original</TableHead><TableHead>Proposed</TableHead><TableHead>Status</TableHead><TableHead>Requested By</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {roleFilteredDeferrals.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No deferrals</TableCell></TableRow>
            ) : roleFilteredDeferrals.map((d: any) => (
              <TableRow key={d.id}>
                <TableCell><p className="text-sm font-display">{d.job_title}</p><p className="text-[10px] text-muted-foreground">{d.job_number}</p></TableCell>
                <TableCell className="text-xs">{d.vessel_name}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate">{d.reason}</TableCell>
                <TableCell className="text-xs">{d.original_due_date || "—"}</TableCell>
                <TableCell className="text-xs text-purple-700">{d.proposed_due_date}</TableCell>
                <TableCell><StatusBadge status={d.status} config={DEFERRAL_STATUS_CONFIG} /></TableCell>
                <TableCell className="text-xs text-muted-foreground">{d.requested_by_name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // ─── COMPLIANCE ─────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderCompliance = () => {
    const completedWOs = roleFilteredWOs.filter((w: any) => w.status === "completed" || w.status === "closed");
    const jsaWOs = roleFilteredWOs.filter((w: any) => w.jsa_required);
    const jsaApproved = jsaWOs.filter((w: any) => w.jsa_approved_by);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Completed WOs", value: completedWOs.length },
            { label: "Class Jobs", value: classJobs.length },
            { label: "JSA Required", value: jsaWOs.length },
            { label: "JSA Approved", value: jsaApproved.length, color: "text-emerald-500" },
          ].map(k => (
            <Card key={k.label}><CardContent className="p-3 text-center"><p className={`text-xl font-bold ${(k as any).color || "text-foreground"}`}>{k.value}</p><p className="text-[10px] text-muted-foreground">{k.label}</p></CardContent></Card>
          ))}
        </div>

        {/* Class Compliance per Vessel */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-red-500" />Class/Statutory Compliance</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {accessibleVessels.map((v: any) => {
              const vClassJobs = classJobs.filter((j: any) => j.vessel_id === v.id);
              const vCompleted = vClassJobs.filter((j: any) => j.status === "completed").length;
              const vOverdue = vClassJobs.filter((j: any) => j.status === "overdue" || (j.status === "scheduled" && j.next_due_date && new Date(j.next_due_date) < new Date())).length;
              const rate = vClassJobs.length > 0 ? Math.round((vCompleted / vClassJobs.length) * 100) : 100;
              return (
                <div key={v.id} className="p-3 rounded-lg border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><Ship className="w-4 h-4 text-teal" /><span className="text-sm font-display">{v.name}</span></div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-emerald-600">{vCompleted} done</span>
                      {vOverdue > 0 && <span className="text-red-500 font-bold">{vOverdue} overdue</span>}
                      <span className={`font-bold ${rate >= 90 ? "text-emerald-600" : rate >= 70 ? "text-amber-500" : "text-red-500"}`}>{rate}%</span>
                    </div>
                  </div>
                  <Progress value={rate} className="h-2" />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* ISM References */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BookOpen className="w-4 h-4 text-teal" />Standards Reference</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {[
              { ref: "ISM Code §10", title: "Maintenance of Ship & Equipment" },
              { ref: "ISM Code §10.2", title: "Critical Equipment Identification" },
              { ref: "SOLAS Ch.III/II-2", title: "LSA & Fire Safety Maintenance" },
              { ref: "DNV Class Rules", title: "PMS Approval for Continuous Survey" },
              { ref: "MARPOL Annex VI", title: "Emission Control Equipment Maintenance" },
            ].map(item => (
              <div key={item.ref} className="flex items-center gap-3 p-2 rounded bg-muted/30">
                <Badge variant="outline" className="text-[9px] shrink-0">{item.ref}</Badge>
                <span className="text-xs">{item.title}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── ASSIGNMENTS ────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderAssignments = () => {
    if (!isTechnicalManager) return (
      <Card><CardContent className="p-12 text-center text-muted-foreground"><Lock className="w-8 h-8 mx-auto mb-3 text-muted-foreground/30" /><p className="font-display">Technical Manager Access Required</p></CardContent></Card>
    );
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center"><h3 className="text-sm font-display">Vessel Access Management</h3><Button onClick={() => setShowAssignDialog(true)} size="sm" className="bg-teal hover:bg-teal-light text-white"><Plus className="w-4 h-4 mr-1" />Assign User</Button></div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vessels.map((v: any) => {
            const assigns = allAssignments.filter((a: any) => a.vessel_id === v.id);
            return (
              <Card key={v.id}><CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3"><Ship className="w-4 h-4 text-teal" /><p className="text-sm font-display">{v.name}</p><Badge variant="outline" className="text-[9px] ml-auto">{assigns.length}</Badge></div>
                {assigns.length === 0 ? <p className="text-xs text-muted-foreground">No users assigned</p> : (
                  <div className="space-y-1">{assigns.map((a: any) => (
                    <div key={a.id} className="flex items-center justify-between p-1.5 rounded bg-muted/30">
                      <div><p className="text-xs">{a.user_id.slice(0, 8)}…</p><p className="text-[9px] text-muted-foreground capitalize">{a.pms_role.replace("_", " ")}</p></div>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-muted-foreground hover:text-red-500" onClick={() => removeAssignment.mutate(a.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}</div>
                )}
              </CardContent></Card>
            );
          })}
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── JOB CARDS / PROCEDURES ─────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderJobCards = () => {
    const classFilteredJobs = filterByVessel(roleFilteredJobs).filter((j: any) =>
      j.title.toLowerCase().includes(search.toLowerCase()) || j.job_number.toLowerCase().includes(search.toLowerCase())
    );

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search job cards..." className="pl-10" /></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {classFilteredJobs.length === 0 ? (
            <Card className="md:col-span-3"><CardContent className="p-12 text-center text-muted-foreground">No job cards found</CardContent></Card>
          ) : classFilteredJobs.map((job: any) => {
            const isSelected = detailType === "job" && detailItem?.id === job.id;
            return (
              <Card key={job.id} className={`cursor-pointer hover:shadow-md transition-all ${isSelected ? "border-teal shadow-md" : ""}`}
                onClick={() => openDetail("job", job)}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <p className="font-display text-sm truncate">{job.title}</p>
                      <p className="text-[10px] text-muted-foreground">{job.job_number} · {job.vessel_name}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {job.class_requirement && <Badge variant="outline" className="text-[8px]">CLASS</Badge>}
                      <PriorityBadge priority={job.priority} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[10px]">
                    <span className="px-2 py-0.5 rounded bg-muted">{JOB_TYPES.find(t => t.value === job.job_type)?.label}</span>
                    <span className="px-2 py-0.5 rounded bg-muted capitalize">{job.department}</span>
                  </div>
                  {job.procedures ? (
                    <p className="text-[10px] text-muted-foreground line-clamp-2">{job.procedures}</p>
                  ) : (
                    <p className="text-[10px] text-amber-600 italic">No procedures documented</p>
                  )}
                  <div className="flex items-center gap-1 pt-1 border-t border-muted text-teal">
                    <BookOpen className="w-3 h-3" />
                    <span className="text-[10px] font-display">View Full Job Card →</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── DIALOGS ────────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  // ═════════════════════════════════════════════════════════════════════════
  // ─── DEFECTS & OBSERVATIONS ─────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const addDefect = useMutation({
    mutationFn: async (form: any) => {
      const vessel = vessels.find((v: any) => v.id === form.vessel_id);
      const eq = equipment.find((e: any) => e.id === form.equipment_id);
      const { error } = await supabase.from("pms_defects").insert({
        ...form, vessel_name: vessel?.name || "", equipment_name: eq?.equipment_name || null,
        reported_by: user?.id, reported_by_name: fullName,
      });
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-defects"] }); toast({ title: "Defect reported" }); setShowAddDefect(false); setDefectForm({ severity: "minor", defect_type: "defect" }); },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateDefect = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { error } = await supabase.from("pms_defects").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["pms-defects"] }); toast({ title: "Defect updated" }); },
  });

  const renderDefects = () => {
    const filteredDefects = filterByVessel(roleFilteredDefects).filter((d: any) =>
      d.title.toLowerCase().includes(search.toLowerCase()) || d.vessel_name.toLowerCase().includes(search.toLowerCase())
    );
    const openDefects = roleFilteredDefects.filter((d: any) => d.status === "open");
    const inProgressDefects = roleFilteredDefects.filter((d: any) => d.status === "in_progress");
    const criticalDefects = roleFilteredDefects.filter((d: any) => d.severity === "critical" && d.status !== "closed");

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search defects..." className="pl-10" /></div>
          <Button onClick={() => setShowAddDefect(true)} size="sm" className="bg-teal hover:bg-teal-light text-white"><Plus className="w-4 h-4 mr-1" />Report Defect</Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Open", value: openDefects.length, color: "text-red-500" },
            { label: "In Progress", value: inProgressDefects.length, color: "text-amber-500" },
            { label: "Critical", value: criticalDefects.length, color: criticalDefects.length > 0 ? "text-red-600" : "text-emerald-500" },
            { label: "Total", value: roleFilteredDefects.length, color: "text-foreground" },
          ].map(k => (
            <Card key={k.label}><CardContent className="p-3 text-center"><p className={`text-xl font-bold ${k.color}`}>{k.value}</p><p className="text-[10px] text-muted-foreground">{k.label}</p></CardContent></Card>
          ))}
        </div>

        {loadingDefects ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div> : (
          <div className="space-y-1.5">
            {filteredDefects.length === 0 ? (
              <Card><CardContent className="p-12 text-center"><Bug className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">No defects found</p></CardContent></Card>
            ) : filteredDefects.map((d: any) => (
              <div key={d.id} className={`rounded-lg border p-3 ${d.severity === "critical" ? "border-red-200 bg-red-50/30 dark:bg-red-500/5" : "border-border"}`}>
                <div className="flex items-center gap-3">
                  <Bug className={`w-5 h-5 shrink-0 ${d.severity === "critical" ? "text-red-600" : d.severity === "major" ? "text-orange-500" : "text-muted-foreground"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display text-sm">{d.title}</p>
                      <StatusBadge status={d.severity} config={DEFECT_SEVERITY_CONFIG} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{d.vessel_name} · {d.equipment_name || "General"} · {format(new Date(d.reported_date), "dd MMM yy")} · By {d.reported_by_name || "—"}</p>
                    {d.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{d.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={d.status} config={DEFECT_STATUS_CONFIG} />
                    {d.status === "open" && (
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => updateDefect.mutate({ id: d.id, updates: { status: "in_progress" } })}>Start</Button>
                    )}
                    {d.status === "in_progress" && (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] text-emerald-600" onClick={() => updateDefect.mutate({ id: d.id, updates: { status: "closed", closed_at: new Date().toISOString(), closed_by: user?.id, closed_by_name: fullName } })}>Close</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── WORK HISTORY ───────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderWorkHistory = () => {
    const completedWOs = filterByVessel(roleFilteredWOs).filter((w: any) => w.status === "completed" || w.status === "closed");
    const completedJobsList = filterByVessel(roleFilteredJobs).filter((j: any) => j.status === "completed" && j.last_completed_at);
    const allHistory = [
      ...completedWOs.map((w: any) => ({ type: "wo" as const, date: w.completed_at || w.closed_at || w.created_at, title: w.title, ref: w.work_order_number, vessel: w.vessel_name, equipment: w.equipment_name, by: w.closed_by_name || w.assigned_to_name, status: w.status, hours: w.actual_hours })),
      ...completedJobsList.map((j: any) => ({ type: "job" as const, date: j.last_completed_at, title: j.title, ref: j.job_number, vessel: j.vessel_name, equipment: "", by: j.last_completed_by_name, status: "completed", hours: null })),
    ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const filteredHistory = allHistory.filter(h =>
      h.title.toLowerCase().includes(search.toLowerCase()) || h.ref.toLowerCase().includes(search.toLowerCase()) || h.vessel.toLowerCase().includes(search.toLowerCase())
    );

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search history..." className="pl-10" /></div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><History className="w-4 h-4" /><span>{allHistory.length} completed items</span></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-foreground">{completedWOs.length}</p><p className="text-[10px] text-muted-foreground">Completed WOs</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-foreground">{completedJobsList.length}</p><p className="text-[10px] text-muted-foreground">Completed Jobs</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-teal">{completedWOs.reduce((s, w) => s + (w.actual_hours || 0), 0).toLocaleString()}h</p><p className="text-[10px] text-muted-foreground">Total Logged Hours</p></CardContent></Card>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Ref</TableHead><TableHead>Title</TableHead><TableHead>Vessel</TableHead>
              <TableHead>Equipment</TableHead><TableHead>By</TableHead><TableHead>Type</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filteredHistory.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No completed work found</TableCell></TableRow>
              ) : filteredHistory.slice(0, 100).map((h, i) => (
                <TableRow key={`${h.ref}-${i}`}>
                  <TableCell className="text-xs">{format(new Date(h.date), "dd MMM yy")}</TableCell>
                  <TableCell className="font-mono text-xs">{h.ref}</TableCell>
                  <TableCell className="text-sm">{h.title}</TableCell>
                  <TableCell className="text-xs">{h.vessel}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{h.equipment || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{h.by || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[9px]">{h.type === "wo" ? "Work Order" : "Job"}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── VESSEL VISIT REPORT ─────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderVISIReport = () => {
    const selectedVessel = visiVessel || (accessibleVessels.length > 0 ? accessibleVessels[0].id : null);
    const vesselData = vessels.find((v: any) => v.id === selectedVessel);
    const totalItems = VISI_CATEGORIES.reduce((s, c) => s + c.items.length, 0);
    const checkedItems = Object.keys(visiChecks).length;
    const passedItems = Object.values(visiChecks).filter(v => v === "pass").length;
    const failedItems = Object.values(visiChecks).filter(v => v === "fail").length;
    const naItems = Object.values(visiChecks).filter(v => v === "na").length;
    const completionPct = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;
    const passRate = checkedItems > 0 ? Math.round((passedItems / (passedItems + failedItems || 1)) * 100) : 0;

    const toggleCheck = (catId: string, itemIdx: number) => {
      const key = `${catId}-${itemIdx}`;
      const current = visiChecks[key];
      const next = !current ? "pass" : current === "pass" ? "fail" : current === "fail" ? "na" : undefined;
      if (next) {
        setVisiChecks({ ...visiChecks, [key]: next });
      } else {
        const copy = { ...visiChecks };
        delete copy[key];
        setVisiChecks(copy);
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Select value={selectedVessel || ""} onValueChange={v => { setVisiVessel(v); setVisiChecks({}); }}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Select vessel" /></SelectTrigger>
            <SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
          </Select>
          {vesselData && <Badge variant="outline" className="text-xs capitalize">{vesselData.status?.replace("_", " ")}</Badge>}
          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <ClipboardCheck className="w-4 h-4 text-teal" />
            <span>Vessel Inspection Report</span>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-foreground">{totalItems}</p><p className="text-[10px] text-muted-foreground">Total Items</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-teal">{completionPct}%</p><p className="text-[10px] text-muted-foreground">Completion</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-600">{passedItems}</p><p className="text-[10px] text-muted-foreground">Pass</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-red-500">{failedItems}</p><p className="text-[10px] text-muted-foreground">Fail</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-muted-foreground">{naItems}</p><p className="text-[10px] text-muted-foreground">N/A</p></CardContent></Card>
        </div>

        {passedItems + failedItems > 0 && (
          <Card><CardContent className="p-4"><div className="flex items-center justify-between mb-2"><span className="text-sm font-display">Pass Rate</span><span className={`text-sm font-bold ${passRate >= 90 ? "text-emerald-600" : passRate >= 70 ? "text-amber-500" : "text-red-500"}`}>{passRate}%</span></div><Progress value={passRate} className="h-3" /></CardContent></Card>
        )}

        {/* Checklist sections */}
        {VISI_CATEGORIES.map(cat => {
          const catPassed = cat.items.filter((_, i) => visiChecks[`${cat.id}-${i}`] === "pass").length;
          const catFailed = cat.items.filter((_, i) => visiChecks[`${cat.id}-${i}`] === "fail").length;
          const catChecked = cat.items.filter((_, i) => visiChecks[`${cat.id}-${i}`]).length;

          return (
            <Card key={cat.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-teal" />{cat.label}</span>
                  <div className="flex items-center gap-3 text-[10px] font-normal text-muted-foreground">
                    <span className="text-emerald-600">{catPassed} ✓</span>
                    {catFailed > 0 && <span className="text-red-500">{catFailed} ✗</span>}
                    <span>{catChecked}/{cat.items.length}</span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 p-3">
                {cat.items.map((item, idx) => {
                  const key = `${cat.id}-${idx}`;
                  const status = visiChecks[key];
                  return (
                    <button key={idx} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-xs transition-colors ${
                      status === "pass" ? "bg-emerald-50 dark:bg-emerald-500/10" : status === "fail" ? "bg-red-50 dark:bg-red-500/10" : status === "na" ? "bg-muted/50" : "hover:bg-muted/30"
                    }`} onClick={() => toggleCheck(cat.id, idx)}>
                      {status === "pass" ? <CheckSquare className="w-4 h-4 text-emerald-600 shrink-0" /> :
                       status === "fail" ? <XCircle className="w-4 h-4 text-red-500 shrink-0" /> :
                       status === "na" ? <MinusSquare className="w-4 h-4 text-muted-foreground shrink-0" /> :
                       <Square className="w-4 h-4 text-muted-foreground/40 shrink-0" />}
                      <span className={status === "na" ? "text-muted-foreground line-through" : status === "fail" ? "text-red-700" : ""}>{item}</span>
                      {status && <span className={`ml-auto text-[9px] font-bold uppercase ${status === "pass" ? "text-emerald-600" : status === "fail" ? "text-red-500" : "text-muted-foreground"}`}>{status}</span>}
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderDialogs = () => (
    <>
      {/* Add Equipment */}
      <Dialog open={showAddEquipment} onOpenChange={setShowAddEquipment}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Register Equipment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Vessel" required><Select value={eqForm.vessel_id || ""} onValueChange={v => setEqForm({ ...eqForm, vessel_id: v })}><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger><SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Equipment Name" required><Input value={eqForm.equipment_name || ""} onChange={e => setEqForm({ ...eqForm, equipment_name: e.target.value })} placeholder="e.g. Main Engine #1" /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Equipment Code"><Input value={eqForm.equipment_code || ""} onChange={e => setEqForm({ ...eqForm, equipment_code: e.target.value })} placeholder="e.g. ME-001" /></FormField>
              <FormField label="Category" required><Select value={eqForm.category || "engine"} onValueChange={v => setEqForm({ ...eqForm, category: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{EQUIPMENT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></FormField>
            </div>
            <FormField label="Sub-System"><Input value={eqForm.sub_system || ""} onChange={e => setEqForm({ ...eqForm, sub_system: e.target.value })} placeholder="e.g. Fuel Injection System" /></FormField>
            <FormField label="Parent Equipment">
              <Select value={eqForm.parent_equipment_id || ""} onValueChange={v => setEqForm({ ...eqForm, parent_equipment_id: v || null })}>
                <SelectTrigger><SelectValue placeholder="None (top-level)" /></SelectTrigger>
                <SelectContent><SelectItem value="">None</SelectItem>{vesselEquipment(eqForm.vessel_id || "").map((e: any) => <SelectItem key={e.id} value={e.id}>{e.equipment_code ? `${e.equipment_code} — ` : ""}{e.equipment_name}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Manufacturer"><Input value={eqForm.manufacturer || ""} onChange={e => setEqForm({ ...eqForm, manufacturer: e.target.value })} /></FormField>
              <FormField label="Model"><Input value={eqForm.model || ""} onChange={e => setEqForm({ ...eqForm, model: e.target.value })} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Serial Number"><Input value={eqForm.serial_number || ""} onChange={e => setEqForm({ ...eqForm, serial_number: e.target.value })} /></FormField>
              <FormField label="Criticality" required><Select value={eqForm.criticality || "B"} onValueChange={v => setEqForm({ ...eqForm, criticality: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CRITICALITY.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Location"><Input value={eqForm.location_on_vessel || ""} onChange={e => setEqForm({ ...eqForm, location_on_vessel: e.target.value })} /></FormField>
              <FormField label="Install Date"><Input type="date" value={eqForm.install_date || ""} onChange={e => setEqForm({ ...eqForm, install_date: e.target.value })} /></FormField>
            </div>
            <FormField label="Notes"><Textarea value={eqForm.notes || ""} onChange={e => setEqForm({ ...eqForm, notes: e.target.value })} /></FormField>
            <Button className="w-full bg-teal hover:bg-teal-light text-white" onClick={() => addEquipment.mutate(eqForm)} disabled={!eqForm.vessel_id || !eqForm.equipment_name}>Register</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Job */}
      <Dialog open={showAddJob} onOpenChange={setShowAddJob}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Maintenance Job</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Vessel" required><Select value={jobForm.vessel_id || ""} onValueChange={v => setJobForm({ ...jobForm, vessel_id: v, equipment_id: "" })}><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger><SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Equipment" required><Select value={jobForm.equipment_id || ""} onValueChange={v => setJobForm({ ...jobForm, equipment_id: v })}><SelectTrigger><SelectValue placeholder="Select equipment" /></SelectTrigger><SelectContent>{vesselEquipment(jobForm.vessel_id || "").map((e: any) => <SelectItem key={e.id} value={e.id}>{e.equipment_code ? `${e.equipment_code} — ` : ""}{e.equipment_name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Job Title" required><Input value={jobForm.title || ""} onChange={e => setJobForm({ ...jobForm, title: e.target.value })} /></FormField>
            <FormField label="Description"><Textarea value={jobForm.description || ""} onChange={e => setJobForm({ ...jobForm, description: e.target.value })} /></FormField>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Type"><Select value={jobForm.job_type} onValueChange={v => setJobForm({ ...jobForm, job_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{JOB_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Priority"><Select value={jobForm.priority} onValueChange={v => setJobForm({ ...jobForm, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Department"><Select value={jobForm.department} onValueChange={v => setJobForm({ ...jobForm, department: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent></Select></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {jobForm.job_type === "calendar" && <FormField label="Interval (Days)"><Input type="number" value={jobForm.interval_days || ""} onChange={e => setJobForm({ ...jobForm, interval_days: parseInt(e.target.value) || null })} /></FormField>}
              {jobForm.job_type === "running_hours" && <FormField label="Interval (Hours)"><Input type="number" value={jobForm.interval_hours || ""} onChange={e => setJobForm({ ...jobForm, interval_hours: parseInt(e.target.value) || null })} /></FormField>}
              <FormField label="Due Date"><Input type="date" value={jobForm.next_due_date || ""} onChange={e => setJobForm({ ...jobForm, next_due_date: e.target.value })} /></FormField>
            </div>
            <FormField label="Procedures"><Textarea value={jobForm.procedures || ""} onChange={e => setJobForm({ ...jobForm, procedures: e.target.value })} placeholder="Step-by-step instructions..." rows={4} /></FormField>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><Switch checked={jobForm.class_requirement || false} onCheckedChange={v => setJobForm({ ...jobForm, class_requirement: v })} /><Label className="text-xs">Class Requirement</Label></div>
              <div className="flex items-center gap-2"><Switch checked={jobForm.requires_alongside || false} onCheckedChange={v => setJobForm({ ...jobForm, requires_alongside: v })} /><Label className="text-xs">Requires Alongside</Label></div>
            </div>
            <Button className="w-full bg-teal hover:bg-teal-light text-white" onClick={() => addJob.mutate(jobForm)} disabled={!jobForm.vessel_id || !jobForm.equipment_id || !jobForm.title}>Create Job</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Work Order */}
      <Dialog open={showAddWO} onOpenChange={setShowAddWO}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Work Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Vessel" required><Select value={woForm.vessel_id || ""} onValueChange={v => setWoForm({ ...woForm, vessel_id: v })}><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger><SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Equipment"><Select value={woForm.equipment_id || ""} onValueChange={v => setWoForm({ ...woForm, equipment_id: v })}><SelectTrigger><SelectValue placeholder="Select equipment" /></SelectTrigger><SelectContent>{vesselEquipment(woForm.vessel_id || "").map((e: any) => <SelectItem key={e.id} value={e.id}>{e.equipment_code ? `${e.equipment_code} — ` : ""}{e.equipment_name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Title" required><Input value={woForm.title || ""} onChange={e => setWoForm({ ...woForm, title: e.target.value })} /></FormField>
            <FormField label="Description"><Textarea value={woForm.description || ""} onChange={e => setWoForm({ ...woForm, description: e.target.value })} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Priority"><Select value={woForm.priority} onValueChange={v => setWoForm({ ...woForm, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Department"><Select value={woForm.department} onValueChange={v => setWoForm({ ...woForm, department: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent></Select></FormField>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><Switch checked={woForm.jsa_required || false} onCheckedChange={v => setWoForm({ ...woForm, jsa_required: v })} /><Label className="text-xs">JSA Required</Label></div>
              <div className="flex items-center gap-2"><Switch checked={woForm.requires_class_survey || false} onCheckedChange={v => setWoForm({ ...woForm, requires_class_survey: v })} /><Label className="text-xs">Class Survey</Label></div>
            </div>
            {woForm.jsa_required && <>
              <FormField label="Hazards"><Textarea value={woForm.jsa_hazards || ""} onChange={e => setWoForm({ ...woForm, jsa_hazards: e.target.value })} placeholder="Identified hazards..." rows={2} /></FormField>
              <FormField label="Control Measures"><Textarea value={woForm.jsa_controls || ""} onChange={e => setWoForm({ ...woForm, jsa_controls: e.target.value })} placeholder="Risk controls..." rows={2} /></FormField>
            </>}
            <Button className="w-full bg-teal hover:bg-teal-light text-white" onClick={() => addWorkOrder.mutate(woForm)} disabled={!woForm.vessel_id || !woForm.title}>Create Work Order</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Spare Part */}
      <Dialog open={showAddPart} onOpenChange={setShowAddPart}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Spare Part</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Part Number" required><Input value={partForm.part_number || ""} onChange={e => setPartForm({ ...partForm, part_number: e.target.value })} /></FormField>
            <FormField label="Description" required><Input value={partForm.description || ""} onChange={e => setPartForm({ ...partForm, description: e.target.value })} /></FormField>
            <FormField label="Vessel"><Select value={partForm.vessel_id || ""} onValueChange={v => setPartForm({ ...partForm, vessel_id: v })}><SelectTrigger><SelectValue placeholder="Shore stock" /></SelectTrigger><SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></FormField>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Qty On Hand"><Input type="number" value={partForm.quantity_on_hand || 0} onChange={e => setPartForm({ ...partForm, quantity_on_hand: parseInt(e.target.value) || 0 })} /></FormField>
              <FormField label="Min Qty"><Input type="number" value={partForm.minimum_quantity} onChange={e => setPartForm({ ...partForm, minimum_quantity: parseInt(e.target.value) || 0 })} /></FormField>
              <FormField label="Max Qty"><Input type="number" value={partForm.maximum_quantity} onChange={e => setPartForm({ ...partForm, maximum_quantity: parseInt(e.target.value) || 0 })} /></FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Reorder Point"><Input type="number" value={partForm.reorder_point} onChange={e => setPartForm({ ...partForm, reorder_point: parseInt(e.target.value) || 0 })} /></FormField>
              <FormField label="Unit Cost"><Input type="number" step="0.01" value={partForm.unit_cost || ""} onChange={e => setPartForm({ ...partForm, unit_cost: parseFloat(e.target.value) || null })} /></FormField>
            </div>
            <div className="flex items-center gap-2"><Switch checked={partForm.is_critical_spare || false} onCheckedChange={v => setPartForm({ ...partForm, is_critical_spare: v })} /><Label className="text-xs">Critical Spare</Label></div>
            <Button className="w-full bg-teal hover:bg-teal-light text-white" onClick={() => addSparePart.mutate(partForm)} disabled={!partForm.part_number || !partForm.description}>Add Part</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Running Hours */}
      <Dialog open={showLogHours} onOpenChange={setShowLogHours}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Log Running Hours</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Vessel" required><Select value={hoursForm.vessel_id || ""} onValueChange={v => setHoursForm({ ...hoursForm, vessel_id: v, equipment_id: "" })}><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger><SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Equipment" required><Select value={hoursForm.equipment_id || ""} onValueChange={v => setHoursForm({ ...hoursForm, equipment_id: v })}><SelectTrigger><SelectValue placeholder="Select equipment" /></SelectTrigger><SelectContent>{vesselEquipment(hoursForm.vessel_id || "").map((e: any) => <SelectItem key={e.id} value={e.id}>{e.equipment_name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Hours Reading" required><Input type="number" value={hoursForm.hours_reading || ""} onChange={e => setHoursForm({ ...hoursForm, hours_reading: parseFloat(e.target.value) || 0 })} /></FormField>
            <FormField label="Date"><Input type="date" value={hoursForm.recorded_date || new Date().toISOString().split("T")[0]} onChange={e => setHoursForm({ ...hoursForm, recorded_date: e.target.value })} /></FormField>
            <Button className="w-full bg-teal hover:bg-teal-light text-white" onClick={() => logRunningHours.mutate(hoursForm)} disabled={!hoursForm.equipment_id || !hoursForm.hours_reading}>Log Hours</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Log Condition Reading */}
      <Dialog open={showAddCondition} onOpenChange={setShowAddCondition}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Log Condition Reading</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Vessel" required><Select value={conditionForm.vessel_id || ""} onValueChange={v => setConditionForm({ ...conditionForm, vessel_id: v, equipment_id: "" })}><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger><SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Equipment" required><Select value={conditionForm.equipment_id || ""} onValueChange={v => setConditionForm({ ...conditionForm, equipment_id: v })}><SelectTrigger><SelectValue placeholder="Select equipment" /></SelectTrigger><SelectContent>{vesselEquipment(conditionForm.vessel_id || "").map((e: any) => <SelectItem key={e.id} value={e.id}>{e.equipment_name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Parameter"><Select value={conditionForm.parameter_type} onValueChange={v => setConditionForm({ ...conditionForm, parameter_type: v, unit: CONDITION_PARAMETER_TYPES.find(p => p.value === v)?.unit || "" })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CONDITION_PARAMETER_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label} ({p.unit})</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Reading Value" required><Input type="number" step="0.01" value={conditionForm.reading_value || ""} onChange={e => setConditionForm({ ...conditionForm, reading_value: parseFloat(e.target.value) || 0 })} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Warning Threshold"><Input type="number" step="0.01" value={conditionForm.threshold_warning || ""} onChange={e => setConditionForm({ ...conditionForm, threshold_warning: parseFloat(e.target.value) || null })} /></FormField>
              <FormField label="Critical Threshold"><Input type="number" step="0.01" value={conditionForm.threshold_critical || ""} onChange={e => setConditionForm({ ...conditionForm, threshold_critical: parseFloat(e.target.value) || null })} /></FormField>
            </div>
            <Button className="w-full bg-teal hover:bg-teal-light text-white" onClick={() => addConditionReading.mutate(conditionForm)} disabled={!conditionForm.equipment_id || !conditionForm.reading_value}>Log Reading</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Defect */}
      <Dialog open={showAddDefect} onOpenChange={setShowAddDefect}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Report Defect / Observation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Vessel" required><Select value={defectForm.vessel_id || ""} onValueChange={v => setDefectForm({ ...defectForm, vessel_id: v })}><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger><SelectContent>{accessibleVessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Equipment (optional)"><Select value={defectForm.equipment_id || ""} onValueChange={v => setDefectForm({ ...defectForm, equipment_id: v || null })}><SelectTrigger><SelectValue placeholder="General" /></SelectTrigger><SelectContent><SelectItem value="">None</SelectItem>{vesselEquipment(defectForm.vessel_id || "").map((e: any) => <SelectItem key={e.id} value={e.id}>{e.equipment_name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="Title" required><Input value={defectForm.title || ""} onChange={e => setDefectForm({ ...defectForm, title: e.target.value })} placeholder="Short description of the defect" /></FormField>
            <FormField label="Description"><Textarea value={defectForm.description || ""} onChange={e => setDefectForm({ ...defectForm, description: e.target.value })} rows={3} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Severity"><Select value={defectForm.severity} onValueChange={v => setDefectForm({ ...defectForm, severity: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(DEFECT_SEVERITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select></FormField>
              <FormField label="Type"><Select value={defectForm.defect_type} onValueChange={v => setDefectForm({ ...defectForm, defect_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{DEFECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></FormField>
            </div>
            <FormField label="Location on Vessel"><Input value={defectForm.location_on_vessel || ""} onChange={e => setDefectForm({ ...defectForm, location_on_vessel: e.target.value })} placeholder="e.g. Engine Room, Port Side" /></FormField>
            <FormField label="Corrective Action (if known)"><Textarea value={defectForm.corrective_action || ""} onChange={e => setDefectForm({ ...defectForm, corrective_action: e.target.value })} rows={2} /></FormField>
            <Button className="w-full bg-teal hover:bg-teal-light text-white" onClick={() => addDefect.mutate(defectForm)} disabled={!defectForm.vessel_id || !defectForm.title}>Report Defect</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deferral Request */}
      <Dialog open={showDeferDialog} onOpenChange={(o) => { setShowDeferDialog(o); if (!o) setDeferJob(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Request Job Deferral</DialogTitle></DialogHeader>
          {deferJob && (
            <div className="space-y-3">
              <Card><CardContent className="p-3"><p className="text-sm font-display">{deferJob.title}</p><p className="text-xs text-muted-foreground">{deferJob.job_number} · {deferJob.vessel_name}</p></CardContent></Card>
              <FormField label="Reason" required><Textarea value={deferForm.reason || ""} onChange={e => setDeferForm({ ...deferForm, reason: e.target.value })} rows={3} /></FormField>
              <FormField label="Risk Assessment"><Textarea value={deferForm.risk_assessment || ""} onChange={e => setDeferForm({ ...deferForm, risk_assessment: e.target.value })} rows={2} /></FormField>
              <FormField label="Proposed Due Date" required><Input type="date" value={deferForm.proposed_due_date || ""} onChange={e => setDeferForm({ ...deferForm, proposed_due_date: e.target.value })} /></FormField>
              <div className="p-3 bg-amber-50 dark:bg-amber-500/10 rounded-lg text-xs text-amber-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div><p className="font-display">Approval Chain: CE → Technical Manager</p></div>
              </div>
              <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" onClick={() => submitDeferral.mutate({
                job_id: deferJob.id, vessel_id: deferJob.vessel_id, vessel_name: deferJob.vessel_name,
                job_number: deferJob.job_number, job_title: deferJob.title,
                requested_by: user?.id, requested_by_name: fullName,
                reason: deferForm.reason, risk_assessment: deferForm.risk_assessment || null,
                original_due_date: deferJob.next_due_date || null, proposed_due_date: deferForm.proposed_due_date,
              })} disabled={!deferForm.reason || !deferForm.proposed_due_date}>Submit Deferral</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign User */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign Crew to Vessel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Crew Member" required>
              <Select value={assignForm.user_id || ""} onValueChange={v => setAssignForm({ ...assignForm, user_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employeeList.map((e: any) => <SelectItem key={e.user_id} value={e.user_id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Vessel" required><Select value={assignForm.vessel_id || ""} onValueChange={v => setAssignForm({ ...assignForm, vessel_id: v })}><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger><SelectContent>{vessels.map((v: any) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></FormField>
            <FormField label="PMS Role"><Select value={assignForm.pms_role || "crew"} onValueChange={v => setAssignForm({ ...assignForm, pms_role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PMS_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></FormField>
            <Button className="w-full bg-teal hover:bg-teal-light text-white" onClick={() => { addVesselAssignment.mutate(assignForm); setShowAssignDialog(false); }} disabled={!assignForm.user_id || !assignForm.vessel_id}>Assign</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Completion (rich) */}
      <PMSJobCompletionDialog
        open={showCompleteDialog}
        onOpenChange={(o) => { setShowCompleteDialog(o); if (!o) setCompleteJob(null); }}
        job={completeJob}
        userId={user?.id}
        fullName={fullName}
      />
    </>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // ─── SECTION CONTENT RENDERER ───────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
   const renderContent = () => {
    switch (activeSection) {
      case "my_jobs": return (
        <PMSMyJobs jobs={roleFilteredJobs} vessels={vessels} userId={user?.id} fullName={fullName}
          onSelectJob={(j) => openDetail("job", j)}
          onStartJob={(j) => { updateJobStatus.mutate({ id: j.id, updates: { status: "in_progress" } }); }}
          onCompleteJob={(j) => { setCompleteJob(j); setShowCompleteDialog(true); }}
          onDeferJob={(j) => { setDeferJob(j); setShowDeferDialog(true); }}
        />
      );
      case "calendar": return (
        <PMSCalendar vessels={vessels} accessibleVessels={accessibleVessels} jobs={filterByVessel(roleFilteredJobs)}
          vesselFilter={vesselFilter} onVesselFilterChange={setVesselFilter}
          isChiefEngineer={isChiefEngineer} isTechnicalManager={isTechnicalManager} userId={user?.id}
          crewMembers={crewMembers} onAssignJob={(jobId, userId, userName) => assignJobToCrew.mutate({ jobId, userId, userName })}
          onSelectJob={(j) => openDetail("job", j)}
        />
      );
      case "dashboard": return renderDashboard();
      case "component_tree": return renderComponentTree();
      case "equipment": return renderEquipment();
      case "job_cards": return renderJobCards();
      case "jobs": return renderJobs();
      case "work_orders": return renderWorkOrders();
      case "condition_monitoring": return renderConditionMonitoring();
      case "running_hours": return renderRunningHours();
      case "deferrals": return renderDeferrals();
      case "spare_parts": return renderSpareParts();
      case "compliance": return renderCompliance();
      case "defects": return renderDefects();
      case "work_history": return renderWorkHistory();
      case "visi_report": return renderVISIReport();
      case "assignments": return renderAssignments();
      default: return renderDashboard();
    }
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── MAIN RENDER ────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <>
      <div className="flex h-[calc(100vh-120px)] -m-6 overflow-hidden">
        {/* Left Sidebar Navigation */}
        <PMSSidebar
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          vesselFilter={vesselFilter}
          onVesselFilterChange={setVesselFilter}
          accessibleVessels={accessibleVessels}
          hasFullFleetAccess={hasFullFleetAccess}
          employeeRole={employeeRole}
          isTechnicalManager={isTechnicalManager}
          overdueCount={overdueJobs.length}
          openWOCount={openWOs.length}
          criticalConditionCount={criticalConditions.length}
          pendingDeferralCount={pendingDeferrals.length}
          lowStockCount={lowStockParts.length}
          totalEquipment={roleFilteredEquipment.length}
          totalJobs={roleFilteredJobs.length}
          openDefectCount={roleFilteredDefects.filter((d: any) => d.status === "open" || d.status === "in_progress").length}
          myJobsCount={roleFilteredJobs.filter((j: any) => j.assigned_to === user?.id && j.status !== "completed").length}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          <ScrollArea className="flex-1">
            <div className="p-6">
              {/* Breadcrumb / Section Header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-1">
                    <span>PMS</span>
                    <ChevronRight className="w-3 h-3" />
                    <span className="text-foreground capitalize">{activeSection.replace(/_/g, " ")}</span>
                    {vesselFilter !== "all" && (
                      <>
                        <ChevronRight className="w-3 h-3" />
                        <span className="text-teal">{accessibleVessels.find(v => v.id === vesselFilter)?.name}</span>
                      </>
                    )}
                  </div>
                  <h1 className="font-display text-xl text-foreground">
                    Planned Maintenance System
                  </h1>
                </div>
                <p className="text-[10px] text-muted-foreground">ISM Code §10 · DNV / ABS / LR</p>
              </div>

              {renderContent()}
            </div>
          </ScrollArea>

          {/* Right Detail Panel - Slide In */}
          {detailType && (
            <PMSDetailPanel
              type={detailType}
              item={detailItem}
              onClose={closeDetail}
              equipment={equipment}
              spareParts={spareParts}
              conditionData={conditionData}
              jobs={jobs}
              workOrders={workOrders}
              onAction={handleDetailAction}
              isChiefEngineer={isChiefEngineer}
              isSuperintendent={isSuperintendent}
            />
          )}
        </div>
      </div>

      {renderDialogs()}
    </>
  );
};

export const EmployeeMaintenancePMSContent = EmployeeMaintenancePMS;

const EmployeeMaintenancePMSPage = () => (
  <IntranetLayout>
    <EmployeeMaintenancePMSContent />
  </IntranetLayout>
);

export default EmployeeMaintenancePMSPage;
